import {
  conformityRegulationOptionsRequest,
  fetchConformityDetail,
  generateConformityV2Request,
  invalidateConformityDeclaration,
  invalidateConformityList,
  previewConformityV2Request,
  updateConformityRecipientsRequest,
  updateCoverNoteRequest,
  type ConformityV2Evaluation,
  type ConformityV2Target,
} from "@/features/material-compliance/api/conformityApi"
import {
  assignDeclarationSupplierRequest,
  fetchDeclarationReadModel,
  fetchDeclarationScope,
  fetchDeclarationStatements,
  fetchDeclarationSubject,
  invalidateDeclaration,
  invalidateDeclarationRows,
  recordOverrideRequest,
  remapArticleRequest,
  resolveDeclarationId,
  retireOverrideRequest,
  retryDeclarationRequest,
  reviewStatementRequest,
  type DeclarationSubject,
} from "@/features/material-compliance/api/declarationsApi"
import {
  importHistoryQuery,
  importsApi,
  invalidateImportFiles,
  type ImportHistoryItem,
} from "@/features/material-compliance/api/importsApi"
import {
  createInquiriesRequest,
  newInquiryLanguage,
  patchInquiryRequest,
  seedInquiryDraftCaches,
  syncPatchedInquiryCaches,
  type InquiryRecipientIn,
} from "@/features/material-compliance/api/inquiryDraftApi"
import {
  addArticleSupplierDirect,
  createCustomerDirect,
  fetchArticleCompliance,
  fetchArticleDetail,
  fetchArticleSuppliers,
  fetchCustomerDetail,
  fetchProductDetail,
  fetchSupplierDetail,
  overrideArticleComplianceDirect,
  patchArticleDirect,
  patchCustomerDirect,
  patchProductDirect,
  patchSupplierDirect,
  removeArticleSupplierDirect,
  updateArticleSupplierDirect,
} from "@/features/material-compliance/api/masterDataWriteApi"
import {
  createNoteRequest,
  invalidateNoteCaches,
} from "@/features/material-compliance/api/notesApi"
import {
  acceptStaleRequest,
  fetchGaps,
  invalidateAnswerWrites,
  overrideAnswerRequest,
} from "@/features/material-compliance/api/questionnaireProcessingApi"
import {
  closeInquiryDirect,
  fetchInquiryDetail,
  inquiryFrameworksQuery,
  invalidateInquiryCaches,
  resolveInquiryId,
  setAutoRemindersDirect,
  type AutoReminderMode,
} from "@/features/material-compliance/api/supplierInquiriesApi"
import {
  saveTemplateSettings,
  syncTemplateSettingsCache,
  type EmailTemplatesPatch,
  type TemplateSettingsPatch,
} from "@/features/material-compliance/api/templateSettingsApi"
import type { StatementGroup } from "@/features/material-compliance/api/types"
import type { GapItemOut } from "@/features/material-compliance/api/types/questionnaireProcessing"
import {
  conformityBatchPath,
  conformityEditPath,
  customerRequestProcessPath,
} from "@/features/material-compliance/components/customer-requests/routes"
import {
  cellStoreAddress,
  statementStoreAddress,
} from "@/features/material-compliance/components/declarations/review/studio/overrides"
import { verdictMeta } from "@/features/material-compliance/components/declarations/review/verdict"
import {
  declarationReviewPath,
  DECLARATIONS_BASE,
} from "@/features/material-compliance/components/declarations/routes"
import { masterDataRecordRoute } from "@/features/material-compliance/components/master-data/masterDataAgentTargets"
import {
  isoDateToUtcMidnight,
  toIsoDateString,
} from "@/features/material-compliance/components/supplier-declarations/inquiry/draft/inquiryDraftMock"
import {
  INQUIRY_BASE,
  inquiryDraftPath,
} from "@/features/material-compliance/components/supplier-declarations/inquiry/routes"
import { importStatusDisplay } from "@/features/material-compliance/utils/importStatus"
import { INQUIRY_REGULATION_KEYS } from "@/features/material-compliance/utils/inquiryScope"
import { supplierInquiriesTabPath } from "@/features/settings/paths"
import { settingsTargetPath } from "@/features/settings/settingsTargets"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import {
  chatFilesApiCalls,
  ConversationFile,
} from "@/services/api/chatFilesService"
import { destinationDisplayName } from "@/services/api/uploadFormatsService"
import { getFlagValue } from "@/utils/featureFlagStore"
import type { QueryClient } from "@tanstack/react-query"
import { AgentAction, ToolCallResult } from "../types"

export interface ToolExecutorDeps {
  /** The active conversation's persisted id, or null if none exists yet. */
  conversationId: string | null
  /** Files attached in this conversation, for resolving a file_id to a name. */
  stagedFiles: ConversationFile[]
  /** Called after a successful route_file, so the composer can update the chip. */
  onFileRouted: (fileId: string, destination: string) => void
  /** Ask the user to confirm; resolves true on Confirm, false on Cancel. */
  requestConfirmation: (title: string, detail: string) => Promise<boolean>
  postAssistantMessage: (text: string) => void
  /**
   * Queue a "Take me there" affordance for AFTER the turn finishes. A write runs
   * between the tool-call round and the round that narrates it, so dispatching
   * here would put the button on screen before the sentence explaining it.
   */
  queueNavigation: (action: AgentAction) => void
  /**
   * Needed by `create_supplier_inquiries`: the composer reads each draft by GET,
   * and a just-created row can 404 under replica lag, so the create response has
   * to seed the same caches `useCreateInquiries` seeds.
   */
  queryClient: QueryClient
  /** Draft language when the model names none and the org's document language
   *  can't be read — mirrors `useCreateInquiries`. */
  uiLanguage: "en" | "de"
  /**
   * Whether the user may write inquiry notes (`mc.inquiries.send`). The chat
   * writes with direct requests, not the permission-guarded hooks, so this is how
   * `close_inquiry` knows a reason would be refused.
   */
  canWriteInquiryNotes: boolean
  translate: (
    key: TranslationKeys,
    dynamicValues?: Record<string, string | number>,
  ) => string
}

type ToolCall = Extract<AgentAction, { type: "tool_call" }>

/** The article fields a create response carries per draft. */
interface InquiryArticleSummary {
  articleId: string
  articleNumber: string | null
  description: string | null
}

/** Confirmation copy for one call, or null to run it without asking. */
type ConfirmPrompt = { title: string; detail: string } | null

interface ToolHandler {
  /**
   * The copy shown before running. Returning null skips the prompt — for a call
   * that will not actually write (a redirect, or an input `run` will reject).
   * Only consulted when the backend marked the tool as needing confirmation.
   */
  confirm?: (
    toolCall: ToolCall,
    deps: ToolExecutorDeps,
  ) => ConfirmPrompt | Promise<ConfirmPrompt>
  run: (
    toolCall: ToolCall,
    deps: ToolExecutorDeps,
  ) => Promise<Record<string, unknown>>
}

/**
 * Execute a blocking client tool call requested by the backend agent loop.
 * Never throws: every failure becomes an error tool result so the model can
 * recover conversationally.
 *
 * Confirmation is gated HERE, on the `requiresConfirmation` flag the backend
 * registry sends, rather than inside each handler. A tool whose description
 * promises the user confirms first is otherwise relying on every future handler
 * author remembering to ask — and a description that claims a gate the code does
 * not enforce is worse than no gate, because it reads as safe.
 */
export async function executeToolCall(
  toolCall: ToolCall,
  deps: ToolExecutorDeps,
): Promise<ToolCallResult> {
  const handler = HANDLERS[toolCall.name]
  if (!handler) {
    return {
      toolCallId: toolCall.toolCallId,
      content: { error: `This app version does not support ${toolCall.name}.` },
    }
  }
  try {
    if (toolCall.requiresConfirmation) {
      const prompt = (await handler.confirm?.(toolCall, deps)) ?? null
      if (prompt) {
        const confirmed = await deps.requestConfirmation(
          prompt.title,
          prompt.detail,
        )
        if (!confirmed) {
          return {
            toolCallId: toolCall.toolCallId,
            content: {
              status: "rejected_by_user",
              note: "The user declined. Do not retry the same change without new instructions.",
            },
          }
        }
      }
    }
    return {
      toolCallId: toolCall.toolCallId,
      content: await handler.run(toolCall, deps),
    }
  } catch (error) {
    return {
      toolCallId: toolCall.toolCallId,
      content: {
        // The server's own refusal (409 NOT_DRAFT, 422 INVALID_TEMPLATE_*) is the
        // useful half: it tells the model which state made the write invalid, so
        // it can explain the no instead of retrying blind.
        error: apiMessage(error) ?? "The action failed in the app.",
      },
    }
  }
}

/** The backend's `detail.message` for a failed write, when there is one. */
function apiMessage(error: unknown): string | null {
  const detail = (
    error as {
      response?: { data?: { detail?: { message?: string } | string } }
    }
  )?.response?.data?.detail
  if (typeof detail === "string") return detail
  return detail?.message ?? null
}

/** `label: value` lines for the fields a write is about to change. */
function changeLines(
  entries: [string, unknown][],
  translate: ToolExecutorDeps["translate"],
): {
  text: string
  count: number
} {
  const lines = entries
    .filter(([, value]) => value !== undefined)
    .map(([label, value]) => `${label}: ${formatValue(value, translate)}`)
  return { text: lines.join("\n"), count: lines.length }
}

function formatValue(
  value: unknown,
  translate: ToolExecutorDeps["translate"],
): string {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : translate("AI_CHAT/VALUE_CLEARED")
  }
  if (value === "" || value === null) {
    return translate("AI_CHAT/VALUE_CLEARED")
  }
  return String(value)
}

const str = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined

/**
 * A model-supplied id list, de-duplicated with order preserved. A single id can
 * arrive as a bare string rather than a list, and a duplicate would be a second
 * write against the same inquiry — for a reminder, a second email.
 */
function idList(value: unknown): string[] {
  const raw = typeof value === "string" ? [value] : value
  if (!Array.isArray(raw)) {
    return []
  }
  const ids = raw
    .map((entry) => String(entry).trim())
    .filter((entry) => entry.length > 0)
  return [...new Set(ids)]
}

/**
 * The record id for a declaration write, from whichever identifier the model is
 * holding. Every screen shows DEC-YYYY-NNNN and never the row id, so that is what
 * gets quoted back — and the write endpoints take the row id only.
 */
async function declarationId(
  input: Record<string, unknown>,
): Promise<string | undefined> {
  const raw = str(input.declaration_id)
  return raw ? await resolveDeclarationId(raw) : undefined
}

/** Renders as the same "Take me there" affordance a navigate action produces. */
function offerNavigation(
  deps: ToolExecutorDeps,
  route: string,
  label: string,
): void {
  deps.queueNavigation({ type: "navigate", route, label })
}

/**
 * A deadline as the "YYYY-MM-DD" the picker works in. A volunteered timestamp is
 * truncated, so both spellings land on the same day. Null when there is no real
 * calendar date to read.
 *
 * Shape is not enough: `2026-02-31` matches the pattern and JS rolls it over to
 * March 3rd, so it would reach the API as a different day than anyone asked for,
 * and `2026-99-99` would 422 there instead of here. The round trip is what
 * catches both — and the NaN guard has to come first, because `toISOString()`
 * throws on an Invalid Date.
 */
function dueDate(value: unknown): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""))
  if (!match) {
    return null
  }
  const iso = `${match[1]}-${match[2]}-${match[3]}`
  const parsed = new Date(`${iso}T00:00:00.000Z`)
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== iso
  ) {
    return null
  }
  return iso
}

// ─── Master data writes ───────────────────────────────────────────────────────

/** The verdict vocabulary the override endpoint accepts (mirrors the backend). */
const VERDICT_VALUES = [
  "compliant",
  "compliant_with_conditions",
  "non_compliant",
  "pending",
  "unknown",
]

/** Master-data entity → the read that returns its current field values. */
const MD_FETCH = {
  supplier: fetchSupplierDetail,
  article: fetchArticleDetail,
  product: fetchProductDetail,
  customer: fetchCustomerDetail,
} as const

/** Master-data entity → the direct PATCH for it. */
const MD_PATCH = {
  supplier: patchSupplierDirect,
  article: patchArticleDirect,
  product: patchProductDirect,
  customer: patchCustomerDirect,
} as const

/** The plural segment `masterDataWriteApi` keys its caches by. */
const MD_CACHE_ENTITY = {
  supplier: "suppliers",
  article: "articles",
  product: "products",
  customer: "customers",
} as const

type MdEntity = keyof typeof MD_FETCH

/**
 * The record's real name, for a dialog title. Falls back to the name the model
 * supplied when the read fails — the user still gets a dialog, just a less
 * authoritative one, which beats blocking a write behind a lookup.
 */
async function recordLabel(
  entity: MdEntity,
  id: string,
  input: Record<string, unknown>,
): Promise<string> {
  try {
    const record = await MD_FETCH[entity](id)
    return record.name ?? str(input.name) ?? id
  } catch {
    return str(input.name) ?? id
  }
}

/** The record as stored, or null when it could not be read. */
async function readRecord(
  entity: MdEntity,
  id: string,
): Promise<Record<string, unknown> | null> {
  try {
    return (await MD_FETCH[entity](id)) as unknown as Record<string, unknown>
  } catch {
    return null
  }
}

/** Null, empty and absent all mean "no value"; strings compare trimmed. */
function sameStoredValue(before: unknown, after: unknown): boolean {
  const normalise = (value: unknown) =>
    value === null || value === undefined || value === ""
      ? ""
      : typeof value === "string"
        ? value.trim()
        : value
  return normalise(before) === normalise(after)
}

/**
 * Of the fields the model supplied, the ones whose value actually differs from
 * what is stored.
 *
 * Asked to set a field no tool can write, the model pads the call with a field
 * it CAN write at its existing value — which used to produce a dialog reading
 * "315421886 → 315421886" and then a successful write of nothing. When the
 * record cannot be read, every supplied field counts: better to write than to
 * refuse on a failed GET.
 */
function changedFields(
  fields: [argName: string, apiField: string, labelKey: TranslationKeys][],
  input: Record<string, unknown>,
  current: Record<string, unknown> | null,
): [argName: string, apiField: string, labelKey: TranslationKeys][] {
  const supplied = fields.filter(([argName]) => isSupplied(input[argName]))
  if (!current) return supplied
  return supplied.filter(
    ([argName, apiField]) =>
      !sameStoredValue(current[apiField], input[argName]),
  )
}

/**
 * Refuse the write when `record_id` does not belong to the record the model
 * named. The model can be working from a remembered result, or reaching for a
 * display field (an article number) instead of the id — either way the write
 * would land on the wrong record, which no confirmation dialog would catch
 * because the dialog is built from the same wrong id.
 *
 * A read that FAILS does not block the write: the id may be right and the
 * network wrong, and the endpoint will 404 on its own if it is not.
 */
async function verifyRecordName(
  entity: MdEntity,
  id: string,
  input: Record<string, unknown>,
  // Passed when the caller has already read the record, so one write does not
  // fetch it twice. `undefined` means "not read yet"; `null` means "read failed".
  current?: Record<string, unknown> | null,
): Promise<Record<string, unknown> | null> {
  const claimed = str(input.name)
  if (!claimed) {
    return {
      error:
        'Missing "name" — the name you believe this record has, for verification.',
    }
  }
  const record = current === undefined ? await readRecord(entity, id) : current
  const actual = record ? ((record.name as string | null) ?? null) : null
  if (!actual || namesMatch(claimed, actual)) {
    return null
  }
  return {
    error:
      `record_id ${id} belongs to "${actual}", not "${claimed}" — this is not the record you ` +
      `meant, and nothing was changed. Look the ${entity} up again and use the id from that ` +
      "fresh result, not a remembered one.",
  }
}

/**
 * `verifyRecordName` for every supplier in a link batch, checked in parallel.
 * Without this, `link_article_suppliers` / `update_article_supplier_link` /
 * `unlink_article_supplier` verified only the ARTICLE's name — a wrong
 * `supplier_id` paired with an unrelated `supplier_name` would write to (or
 * remove) the wrong company's link, and no confirmation dialog would catch it
 * either, because the dialog is built from the same model-supplied name.
 */
async function verifySupplierNames(
  links: { supplierId: string; supplierName: string }[],
): Promise<Record<string, unknown> | null> {
  const results = await Promise.all(
    links.map((link) =>
      verifyRecordName("supplier", link.supplierId, {
        name: link.supplierName,
      }),
    ),
  )
  return (
    results.find(
      (result): result is Record<string, unknown> => result !== null,
    ) ?? null
  )
}

/**
 * Loose enough for punctuation and legal-form drift, strict enough to catch a
 * different company.
 *
 * Compares TOKEN SETS, not substrings, and splits on a Unicode-aware class.
 * Both details are load-bearing, because this function is the guard that stops a
 * write landing on the wrong record:
 *
 * - `[^a-z0-9]` would erase a Cyrillic, Greek or CJK name to the empty string,
 *   and every string contains `""` — so a substring test would report two
 *   completely unrelated records as the same one, in exactly the scripts where a
 *   human reviewer is least likely to notice.
 * - Substring matching is too weak even on ASCII: a one-character claimed name
 *   is a substring of almost anything ("a" inside "Wiha GmbH"). Requiring the
 *   smaller token set to be contained in the larger keeps "Wiha" ≡ "Wiha
 *   Werkzeuge GmbH" while rejecting "ACME Corp" ≢ "ACME Industries".
 */
export function namesMatch(claimed: string, actual: string): boolean {
  const tokens = (value: string) =>
    new Set(
      value
        .normalize("NFKC")
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .filter(Boolean),
    )
  const a = tokens(claimed)
  const b = tokens(actual)
  // A name with no letters or digits at all cannot be verified, so it must not
  // pass — an unverifiable claim is a mismatch, not a match.
  if (a.size === 0 || b.size === 0) {
    return false
  }
  const [smaller, larger] = a.size <= b.size ? [a, b] : [b, a]
  return [...smaller].every((token) => larger.has(token))
}

/** One supplier link from the model's `suppliers` array. */
interface SupplierLinkIn {
  supplierId: string
  supplierName: string
  supplierArticleNumber: string | null
}

function supplierLinks(input: Record<string, unknown>): SupplierLinkIn[] {
  const raw = Array.isArray(input.suppliers) ? input.suppliers : []
  const bySupplier = new Map<string, SupplierLinkIn>()
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue
    const row = entry as Record<string, unknown>
    const supplierId = str(row.supplier_id)
    if (!supplierId || bySupplier.has(supplierId)) continue
    bySupplier.set(supplierId, {
      supplierId,
      supplierName: str(row.supplier_name) ?? supplierId,
      supplierArticleNumber: str(row.supplier_article_number) ?? null,
    })
  }
  return [...bySupplier.values()]
}

/**
 * One field-edit handler, for the four entities whose panels are the same shape.
 *
 * `fields` maps the tool's argument name → the API's field name → the label the
 * dialog shows. The dialog is built from the record read back from the server,
 * not from the model's arguments, so the user sees "current → new" against the
 * real stored value; `consequenceKeys` adds a sentence for the fields whose
 * effect a diff of two numbers does not convey.
 */
function masterDataFieldHandler(config: {
  entity: MdEntity
  fields: [argName: string, apiField: string, labelKey: TranslationKeys][]
  consequenceKeys?: Record<string, TranslationKeys>
}): ToolHandler {
  const { entity, fields, consequenceKeys = {} } = config
  const supplied = (input: Record<string, unknown>) =>
    fields.filter(([argName]) => isSupplied(input[argName]))

  return {
    confirm: async (toolCall, deps) => {
      const id = str(toolCall.input.record_id)
      if (!id || supplied(toolCall.input).length === 0) return null
      // A failed read still gets a dialog showing only the new values, which
      // lets the user approve or reject — better than refusing to ask. The diff
      // takes the unforced value so it agrees with `run` about what changes.
      const stored = await readRecord(entity, id)
      const current = stored ?? {}
      const changing = changedFields(fields, toolCall.input, stored)
      // No dialog when `run` will report a no-op: asking someone to approve a
      // change that is not a change teaches them the dialog means nothing.
      if (changing.length === 0) return null
      const lines = changing.map(([argName, apiField, labelKey]) => {
        const before = current[apiField]
        const after = toolCall.input[argName]
        return [
          deps.translate(labelKey),
          before === undefined || before === null || before === ""
            ? `${deps.translate("AI_CHAT/VALUE_EMPTY")} → ${formatValue(after, deps.translate)}`
            : `${formatValue(before, deps.translate)} → ${formatValue(after, deps.translate)}`,
        ] as [string, unknown]
      })
      const { text } = changeLines(lines, deps.translate)
      const notes = changing
        .map(([argName]) => consequenceKeys[argName])
        .filter((key): key is TranslationKeys => Boolean(key))
        .map((key) => deps.translate(key))
      const record =
        (current.name as string | undefined) ?? str(toolCall.input.name) ?? id
      return {
        title: deps.translate("AI_CHAT/CONFIRM_MD_UPDATE_TITLE", { record }),
        detail: [
          deps.translate("AI_CHAT/CONFIRM_MD_UPDATE_DETAIL"),
          text,
          ...notes,
        ]
          .filter(Boolean)
          .join("\n\n"),
      }
    },
    run: async (toolCall, deps) => {
      const id = str(toolCall.input.record_id)
      if (!id) {
        return { error: `Missing "record_id" — the ${entity} to change.` }
      }
      const given = supplied(toolCall.input)
      if (given.length === 0) {
        return {
          error:
            `No fields to change were given. Name at least one of: ` +
            fields.map(([argName]) => argName).join(", ") +
            ".",
        }
      }
      const current = await readRecord(entity, id)
      const mismatch = await verifyRecordName(
        entity,
        id,
        toolCall.input,
        current,
      )
      if (mismatch) return mismatch
      const changing = changedFields(fields, toolCall.input, current)
      if (changing.length === 0) {
        // Not an error: the record already reads the way the user asked for. A
        // PATCH here would return 200 and report an update that changed nothing.
        return {
          status: "no_change",
          entity,
          record_id: id,
          name: current?.name ?? null,
          already_correct: given.map(([, apiField]) => apiField),
          note:
            "Nothing was written — every field given already had that value. Say so plainly, and " +
            "if the user asked for a field that is not in this tool's schema, tell them it cannot " +
            "be edited here rather than reporting an update.",
        }
      }
      const changes: Record<string, unknown> = {}
      for (const [argName, apiField] of changing) {
        changes[apiField] = toolCall.input[argName]
      }
      const saved = await MD_PATCH[entity](deps.queryClient, id, changes)
      offerNavigation(
        deps,
        masterDataRecordRoute(entity, id),
        saved.name ?? `this ${entity}`,
      )
      return {
        status: "updated",
        entity,
        record_id: id,
        name: saved.name,
        changed: changing.map(([, apiField]) => apiField),
        cache_entity: MD_CACHE_ENTITY[entity],
        note: "Only the fields listed in `changed` were written. Report those, not the whole record.",
      }
    },
  }
}

/** Shared so both write paths refuse an unreadable date in the same words. */
function unreadableDateError(value: unknown): string {
  return (
    `Could not read "${String(value)}" as a date. Use YYYY-MM-DD, and check the day ` +
    "exists — there is no 31st of February."
  )
}

/** Today as "YYYY-MM-DD" — these compare lexicographically, no Date detour. */
function todayIso(): string {
  return toIsoDateString(new Date())
}

/** An expired deadline goes out lapsed and trips the first reminder at once. */
function pastDeadlineError(due: string): string {
  return (
    `${due} is in the past (today is ${todayIso()}). A deadline that has already lapsed ` +
    "goes out expired and fires the first automatic reminder at once, so the app's own date " +
    "picker will not accept one. Ask the user which date they meant."
  )
}

/**
 * The `suppliers` argument as create recipients, de-duplicated by supplier id.
 * Article scope stays PER supplier: one shared list would put one supplier's
 * parts on another's questionnaire, which the app's picker cannot do.
 */
function supplierScopes(input: Record<string, unknown>): InquiryRecipientIn[] {
  const entries = Array.isArray(input.suppliers) ? input.suppliers : []
  const bySupplier = new Map<string, InquiryRecipientIn>()
  for (const entry of entries) {
    // A bare id instead of an object is a plausible slip; read it anyway.
    const record: unknown =
      typeof entry === "string" ? { supplier_id: entry } : entry
    if (typeof record !== "object" || record === null) {
      continue
    }
    const fields = record as { supplier_id?: unknown; article_ids?: unknown }
    const supplierId = String(fields.supplier_id ?? "").trim()
    if (!supplierId || bySupplier.has(supplierId)) {
      continue
    }
    const articleIds = idList(fields.article_ids)
    bySupplier.set(supplierId, {
      supplierId,
      // `.length`, not truthiness: the endpoint reads `[]` as an EXPLICIT empty
      // scope, shipping a questionnaire with no articles on it.
      ...(articleIds.length ? { articleIds } : {}),
    })
  }
  return [...bySupplier.values()]
}

/**
 * What each new draft will actually ask about. Echoed when the scope was narrowed,
 * because the endpoint accepts another supplier's article and still returns 200 —
 * naming the parts back is the only check before the drafts are sent. An id that
 * matched nothing stores with no number and no description, so it goes out blank.
 */
function resolvedScope(
  created: {
    supplierName: string | null
    articles?: InquiryArticleSummary[]
  }[],
  narrowed: boolean,
): Record<string, unknown> {
  const unresolved = created.flatMap((draft) =>
    (draft.articles ?? [])
      .filter((a) => !a.articleNumber && !a.description)
      .map((a) => a.articleId),
  )
  return {
    ...(narrowed
      ? {
          scope_per_supplier: created.map((draft) => ({
            supplier_name: draft.supplierName,
            articles: (draft.articles ?? []).map(
              (a) => a.articleNumber ?? a.articleId,
            ),
          })),
        }
      : {}),
    ...(unresolved.length ? { unresolved_articles: unresolved } : {}),
  }
}

/**
 * The deadline on freshly created drafts, via the same PATCH the picker makes
 * (create takes none). One request each, so a partial failure is real: those
 * drafts still send, with the default window.
 */
async function applyDeadline(
  qc: QueryClient,
  ids: string[],
  due: string,
): Promise<Record<string, unknown>> {
  const dueAt = isoDateToUtcMidnight(due)
  if (!dueAt) {
    return {}
  }
  const outcomes = await Promise.allSettled(
    ids.map((id) =>
      patchInquiryRequest(id, { dueAt }).then((data) => {
        syncPatchedInquiryCaches(qc, id, data)
      }),
    ),
  )
  const failed = ids.filter((_, index) => outcomes[index].status === "rejected")
  return {
    deadline: due,
    ...(failed.length
      ? {
          deadline_not_applied: failed,
          deadline_note:
            `The deadline could not be saved on ${failed.length} draft(s), which keep the ` +
            "default window. Say which, and that it can still be set with the date picker in " +
            "the composer.",
        }
      : {}),
  }
}

// ─── The declaration review ───────────────────────────────────────────────────

/** One part number the document cited, and the article of ours it means. */
interface ArticleMapping {
  supplierCode: string
  articleNumber: string
}

/**
 * The `mappings` argument, de-duplicated by part number. A duplicate would be a
 * second remap of the same code — the last one silently winning.
 */
function articleMappings(input: Record<string, unknown>): ArticleMapping[] {
  const raw = input.mappings
  const entries = Array.isArray(raw) ? raw : raw ? [raw] : []
  const byCode = new Map<string, ArticleMapping>()
  for (const entry of entries) {
    if (typeof entry !== "object" || entry === null) {
      continue
    }
    const fields = entry as {
      supplier_code?: unknown
      article_number?: unknown
    }
    const supplierCode = String(fields.supplier_code ?? "").trim()
    const articleNumber = String(fields.article_number ?? "").trim()
    if (!supplierCode || !articleNumber || byCode.has(supplierCode)) {
      continue
    }
    byCode.set(supplierCode, { supplierCode, articleNumber })
  }
  return [...byCode.values()]
}

/** Said once, wherever a write needs the supplier's own articles to exist. */
const NO_SUPPLIER_ERROR =
  "This declaration is not matched to a supplier yet, so it has no article catalogue to work " +
  "against — and it cannot be approved either, whatever else is resolved. Resolve the supplier " +
  "first with assign_declaration_supplier."

/**
 * Why this declaration cannot take a review write, or null when it can. The
 * endpoints are wider than the screens here: an approved declaration's review is
 * closed (the list routes those rows to the read-only panel, never to the review)
 * and an unread one has no statements to write against yet.
 */
function reviewClosedError(subject: DeclarationSubject): string | null {
  if (subject.approvedAt) {
    return (
      "This declaration is already approved, so its review is closed and its values are in " +
      "master data. Nothing about it can be changed now — a correction means a new declaration."
    )
  }
  if (subject.lifecycleStatus === "failed") {
    return (
      "Extraction failed on this declaration, so there is nothing to review — it has no " +
      "statements and will never grow any. retry_declaration_extraction is the only move."
    )
  }
  if (
    subject.processingStatus === "pending" ||
    subject.processingStatus === "processing"
  ) {
    return (
      `The document is still being read (${subject.processingStatus}), so its statements and ` +
      "coverage do not exist yet. Nothing can be reviewed until extraction lands."
    )
  }
  return null
}

/** One statement, located by the id a review read handed the model. */
interface StatementFacts {
  framework: string
  groupIndex: number
  /** EFFECTIVE: the reviewer's edit where there is one, the extraction's own answer where not. */
  verdict: string | null
  /** False means a human has APPLIED a subset of articles — step 2's signature. */
  scopeAll: boolean
  articleNumbers: string[]
  /** The note in force at the STATEMENT's own address, if any. */
  overrideKind: string | null
}

function locateStatement(
  groups: StatementGroup[],
  statementId: string,
): StatementFacts | null {
  for (const group of groups) {
    const entries = group.groups ?? []
    for (let index = 0; index < entries.length; index++) {
      const entry = entries[index]
      if (entry.statementId !== statementId) {
        continue
      }
      return {
        framework: group.frameworkSlug,
        // The store addresses by POSITION, which is what survives a
        // re-extraction — not by the staged row id the model was given.
        groupIndex: index,
        verdict: entry.verdictOverride ?? entry.values?.verdict ?? null,
        scopeAll: entry.scopeAll,
        articleNumbers: entry.articleNumbers ?? [],
        overrideKind: entry.override?.kind ?? null,
      }
    }
  }
  return null
}

const UNKNOWN_STATEMENT_ERROR =
  "No statement with that id on this declaration. Statement ids come from " +
  "get_declaration_review, and they change when a document is re-extracted — read it again."

/** The override kinds the app offers as a gesture. */
const OVERRIDE_KINDS = [
  "ignored",
  "proceed_unknown",
  "excluded",
  "suppressed",
] as const

type OverrideKind = (typeof OVERRIDE_KINDS)[number]

/** How each kind reads in the confirmation, in the words the control uses. */
const OVERRIDE_KIND_SENTENCE_KEYS: Record<OverrideKind, TranslationKeys> = {
  ignored: "AI_CHAT/OVERRIDE_SENTENCE_IGNORED",
  proceed_unknown: "AI_CHAT/OVERRIDE_SENTENCE_PROCEED_UNKNOWN",
  excluded: "AI_CHAT/OVERRIDE_SENTENCE_EXCLUDED",
  suppressed: "AI_CHAT/OVERRIDE_SENTENCE_SUPPRESSED",
}

function overrideKind(value: unknown): OverrideKind | null {
  return OVERRIDE_KINDS.find((kind) => kind === value) ?? null
}

/** M2's per-statement unmatched part numbers, keyed `framework:groupIndex`. */
async function unresolvedRefs(
  declarationId: string,
  facts: StatementFacts,
): Promise<string[]> {
  const readModel = await fetchDeclarationReadModel(declarationId)
  const key = `${facts.framework}:${facts.groupIndex}`
  return readModel.scope_resolutions?.[key]?.unresolved_refs ?? []
}

/** Whether a note already stands at one address — one is in force per address. */
async function noteAt(
  declarationId: string,
  address: string,
): Promise<string | null> {
  const readModel = await fetchDeclarationReadModel(declarationId)
  return readModel.overrides?.[address]?.kind ?? null
}

/**
 * Is this article one the statement covers? An applied subset says so directly;
 * a statement that still covers everything fans out over the supplier's whole
 * pool, so that is what the number has to be in. Without the check a typo parks
 * an address nothing publishes from — a note that closes nothing.
 */
async function articleCovered(
  declarationId: string,
  facts: StatementFacts,
  articleNumber: string,
): Promise<boolean> {
  if (!facts.scopeAll) {
    return facts.articleNumbers.includes(articleNumber)
  }
  const scope = await fetchDeclarationScope(declarationId)
  return (scope.articlePool ?? []).some(
    (article) => article.articleNumber === articleNumber,
  )
}

/**
 * The state each kind is only offered in, checked HERE because the store does
 * not check it: a note whose paired fact does not hold closes nothing, and turns
 * up on the approval refusal as a mismatch — the reviewer believing an item is
 * taken over when it is not. These are the same rules the review screens' own
 * option lists apply.
 */
async function overrideRefusal(
  declarationId: string,
  kind: OverrideKind,
  facts: StatementFacts,
  articleNumber: string | undefined,
  supplierId: string | null,
): Promise<string | null> {
  if (kind === "ignored") {
    if (!supplierId) {
      // The articles it could park come from the supplier's pool, so without one
      // there is nothing to check the number against.
      return NO_SUPPLIER_ERROR
    }
    if (!articleNumber) {
      return (
        'An "ignored" note parks ONE article of this statement, so it needs article_number. ' +
        'To park the whole statement, "excluded" leaves it out of the approval.'
      )
    }
    if (!(await articleCovered(declarationId, facts, articleNumber))) {
      return (
        `${articleNumber} is not one of the articles this statement covers, so parking it would ` +
        "record a note against nothing. Read the statement's articles in get_declaration_review."
      )
    }
    const standing = await noteAt(
      declarationId,
      cellStoreAddress(facts.framework, articleNumber),
    )
    return standing
      ? `${articleNumber} is already parked as "${standing}" on this statement. ` +
          "Hand that back with retire_declaration_override first, or leave it as it is."
      : null
  }
  if (articleNumber) {
    return (
      `article_number belongs only on an "ignored" note — "${kind}" is about the whole ` +
      'statement, so drop it or use "ignored" instead.'
    )
  }
  if (facts.overrideKind) {
    return (
      `There is already a "${facts.overrideKind}" note on this statement, and one note stands ` +
      "per address — recording another would destroy it. Hand the current one back with " +
      "retire_declaration_override first, or leave it as it is."
    )
  }
  if (kind === "proceed_unknown" && facts.verdict !== "unknown") {
    return (
      `This statement's verdict is "${facts.verdict ?? "not set"}", not unknown, so there is ` +
      "nothing to proceed over — the app offers that kind only while the verdict IS unknown, and " +
      "a note recorded now would read as already retired."
    )
  }
  if (kind === "suppressed") {
    if (facts.scopeAll) {
      return (
        "Nothing is being suppressed: this statement's coverage has not been applied yet, so " +
        'there are no accepted-but-unmatched references to accept. "suppressed" only holds once ' +
        "Apply has been pressed with part numbers still outstanding."
      )
    }
    if ((await unresolvedRefs(declarationId, facts)).length === 0) {
      return (
        "Every part number this statement cites resolved to one of our articles, so there is " +
        'nothing outstanding for a "suppressed" note to accept.'
      )
    }
  }
  return null
}

/** Both override addresses, from the flow's ids — the seam the store keys by. */
function overrideAddressFor(
  facts: StatementFacts,
  articleNumber: string | undefined,
): string {
  return articleNumber
    ? cellStoreAddress(facts.framework, articleNumber)
    : statementStoreAddress(facts.framework, facts.groupIndex)
}

/**
 * What is about to be parked, named the way the review names it, read from the
 * server rather than echoed from the model's arguments. A read that fails degrades
 * to the id: the user still gets a dialog, just a less informative one.
 */
async function describeOverrideTarget(
  declarationId: string,
  statementId: string,
  articleNumber: string | undefined,
  translate: ToolExecutorDeps["translate"],
): Promise<string> {
  try {
    const facts = locateStatement(
      await fetchDeclarationStatements(declarationId),
      statementId,
    )
    if (!facts) {
      return statementId
    }
    const statement = facts.verdict
      ? `${facts.framework} · ${translate(verdictMeta(facts.verdict).labelKey)}`
      : facts.framework
    return articleNumber ? `${statement} → ${articleNumber}` : statement
  } catch {
    return statementId
  }
}

/**
 * Which declaration a dialog is about, in the terms the list shows.
 *
 * A dialog approving an irreversible action has to show the action, not a pronoun
 * — the same rule the reminder-send dialog follows. "Set this declaration's
 * supplier?" named nothing, so a model that picked the wrong one of two
 * same-supplier same-day documents produced a confirmation the user could not
 * tell from the right one. Read from the SERVER, never from the model's
 * arguments: the point is to show what the id actually resolves to.
 *
 * Degrades to the id rather than blocking the write behind a read.
 */
async function describeDeclaration(declarationId: string): Promise<string> {
  try {
    const subject = await fetchDeclarationSubject(declarationId)
    const parts = [subject.declarationNumber, subject.supplier].filter(Boolean)
    return parts.length ? parts.join(" — ") : declarationId
  } catch {
    return declarationId
  }
}

// ── Customer Studio: the intake wizard's answers and the conformity editor ───

/** Items named in a confirmation or a result, before the list is summarised. */
const MAX_LISTED_ITEMS = 8

/**
 * One answer cell, addressed the way `list_customer_request_gaps` reports it and
 * the way a person talks about it: THEIR part number plus the question. Never the
 * answer id — that is what the endpoint takes, and the seam converts, because an
 * id the model carried over from an earlier read can address a different cell
 * after the answering pipeline has re-run.
 */
interface AnswerAddress {
  part: string
  canonicalId: string
  value?: string
}

function answerAddresses(
  input: Record<string, unknown>,
  { withValue }: { withValue: boolean },
): AnswerAddress[] {
  const raw = input.answers
  const entries = Array.isArray(raw) ? raw : raw ? [raw] : []
  // De-duplicated: a repeated address is a second write over the first, and for a
  // typed value the later one silently wins. `::` is the separator `columnKey`
  // already uses in this feature, and cannot collide here: canonical ids are a
  // closed enum with no colons in it.
  const byAddress = new Map<string, AnswerAddress>()
  for (const entry of entries) {
    if (!entry || typeof entry !== "object") continue
    const row = entry as Record<string, unknown>
    const part = str(row.customer_article_number)
    const canonicalId = str(row.canonical_id)
    const value = str(row.value)
    if (!part || !canonicalId || (withValue && !value)) continue
    byAddress.set(`${part}::${canonicalId}`, {
      part,
      canonicalId,
      ...(withValue ? { value } : {}),
    })
  }
  return [...byAddress.values()]
}

interface AnswerHit {
  address: AnswerAddress
  gap: GapItemOut
}

interface AnswerFailure {
  part: string
  question: string
  reason: string
}

/**
 * Resolve every address against the request's own answer list, and refuse the
 * whole call when the request is not in a state that accepts any of it. The
 * endpoints gate on exactly these two pipeline states; saying so here names the
 * stage the request IS at, which their 409 does not.
 */
async function locateAnswers(
  input: Record<string, unknown>,
  addresses: AnswerAddress[],
  { withValue }: { withValue: boolean },
): Promise<
  | { error: string }
  | {
      requestId: string
      found: AnswerHit[]
      failed: AnswerFailure[]
    }
> {
  const requestId = str(input.request_id)
  if (!requestId) {
    return {
      error:
        'Missing "request_id". Get one from list_customer_requests, and the answer addresses from ' +
        "list_customer_request_gaps.",
    }
  }
  if (addresses.length === 0) {
    return {
      error:
        'Missing "answers". Each one needs a customer_article_number and a canonical_id from ' +
        "list_customer_request_gaps" +
        (withValue ? ", plus the value to write." : "."),
    }
  }
  const gaps = await fetchGaps(requestId)
  if (!GAP_WRITABLE_STATUSES.includes(gaps.processingStatus ?? "")) {
    return {
      error:
        `This request is at "${gaps.processingStatus ?? "the start"}", and answers can only be changed ` +
        "while it is in review — the app offers no editing before the answering pipeline has run, or " +
        "while it is running. Say what stage it is at instead of retrying.",
    }
  }
  const all = [...gaps.missing, ...gaps.stale, ...gaps.answered]
  const found: AnswerHit[] = []
  const failed: AnswerFailure[] = []
  for (const address of addresses) {
    const gap = all.find(
      (row) =>
        row.customerArticleNumber === address.part &&
        row.canonicalId === address.canonicalId,
    )
    if (gap) {
      found.push({ address, gap })
    } else {
      failed.push({
        part: address.part,
        question: address.canonicalId,
        reason:
          "No such answer cell on this request — that part number and question do not pair up here. " +
          "Read list_customer_request_gaps again and use its own wording for both.",
      })
    }
  }
  return { requestId, found, failed }
}

/** The two pipeline states `mc_answering_service` accepts any answer write in. */
const GAP_WRITABLE_STATUSES = ["review_ready", "answered"]

/** Trimmed and lowercased, the way `_normalise_email_list` stores it — so
 *  "already on the list" is decided the same way on both sides of the wire. */
function normaliseEmail(value: string): string {
  return value.trim().toLowerCase()
}

/** The full recipient line: the customer's own contact, then the extras. Both
 *  halves receive the declaration, so reporting only the extras would understate
 *  who a send reaches. */
function recipientsNow(primary: string | null, extras: string[]): string[] {
  return primary ? [primary, ...extras] : [...extras]
}

/**
 * The generate body, resolved the way the Generate screen resolves it: the scope
 * decides which id list applies, and the framework has to be one this
 * organization has actually configured — an unconfigured one is missing from the
 * picker, and the endpoint refuses it.
 *
 * `article` scope is not offered. The endpoint accepts it and its tile is
 * commented out on screen (article-level compliance can only be confirmed by
 * suppliers), so chat must not be the only way to reach it.
 */
/**
 * The targets the v2 Generate screen would build for this request
 * (`ConformityGenerateModuleV2`): `customer` is ONE document per customer
 * covering every product they buy, `products` is ONE document covering the
 * products named.
 *
 * `regulation_keys` is the regulations the user named (TUR-1733), checked and
 * closed by the server; without it a declaration covers the org set (and a
 * customer's own regulations), as the screen preselects. Each one is its own
 * deselectable block in the editor.
 */
async function conformityTargets(
  input: Record<string, unknown>,
): Promise<{ error: string } | { targets: ConformityV2Target[] }> {
  const requested = str(input.scope)
  // The screen's own deep links still say `scope=product`, and the model
  // follows the singular too; both mean the multi-select products tab.
  const scope = requested === "product" ? "products" : requested
  if (scope !== "customer" && scope !== "products") {
    return {
      error:
        `Unknown scope ${JSON.stringify(requested)}. Use "customer" (one declaration per customer, ` +
        'covering every product they buy) or "products" (one declaration covering the products you ' +
        "name). Those are different documents — ask which the user means.",
    }
  }
  const ids = idList(
    scope === "products" ? input.product_ids : input.customer_ids,
  )
  if (ids.length === 0) {
    return {
      error:
        scope === "customer"
          ? 'Missing "customer_ids". Resolve them with list_customers.'
          : 'Missing "product_ids". Resolve them with list_products.',
    }
  }
  const regulationKeys = idList(input.regulation_keys).map((key) =>
    key.toUpperCase(),
  )
  // A list the caller passed but left empty is a pick of nothing, not "no pick": the
  // org's set is for a call that leaves the field out, so it is refused as the
  // server refuses one, not quietly answered with a different set.
  if (Array.isArray(input.regulation_keys) && regulationKeys.length === 0) {
    return { error: EMPTY_PICK_MESSAGE }
  }
  // Without a pick, the org-configuration check the screen makes too (its v2
  // evaluation reports the same state as `no_frameworks_configured`, disables
  // Generate, and the server 409s as a backstop). A pick is checked by the server.
  if (regulationKeys.length === 0) {
    const options = await conformityRegulationOptionsRequest(null)
    if (!options.some((o) => o.inOrgSet && o.pickable)) {
      return {
        error:
          "This organization has no conformity frameworks configured, so no declaration can be issued " +
          "yet — the regulations and their customer-facing statement wording are set up in the admin " +
          "backoffice, not here.",
      }
    }
  }
  const pick = regulationKeys.length > 0 ? { regulationKeys } : {}
  // Customer scope issues one document per customer, so several customers are
  // several targets. Products scope is a single multi-select document.
  return {
    targets:
      scope === "customer"
        ? ids.map((customerId) => ({
            scope: "customer" as const,
            customerId,
            ...pick,
          }))
        : [{ scope: "products" as const, subjectIds: ids, ...pick }],
  }
}

/** What the model is told for an empty pick: the server's refusal of one, and the
 *  local refusal of an explicitly empty `regulation_keys`. */
const EMPTY_PICK_MESSAGE =
  "No regulation was picked. Omit regulation_keys to cover the organization's own set."

/** Why the server refused a regulation pick (TUR-1733), naming the keys, so the
 *  model can correct the pick instead of retrying it. Null for anything else. */
function regulationPickRefusal(error: unknown): string | null {
  const detail = (
    error as {
      response?: { data?: { detail?: { code?: string; keys?: string[] } } }
    }
  )?.response?.data?.detail
  const keys = detail?.keys?.join(", ") ?? ""
  switch (detail?.code) {
    case "EMPTY_REGULATION_PICK":
      return EMPTY_PICK_MESSAGE
    case "INVALID_REGULATION_KEY":
      return `Unknown regulation key(s): ${keys}. Use the keys from list_conformity_frameworks.`
    case "REGULATION_NOT_PICKABLE":
      return (
        `${keys} cannot be declared: outside the organization's set and not set up for ` +
        "declarations. Check list_conformity_frameworks and tell the user."
      )
    default:
      return null
  }
}

/** Every target's preview, each one settled: the evaluations when all came back,
 *  and the first refused pick among them whatever failed first, so another
 *  target's network error can never hide a refusal (`Promise.all` would). */
async function previewEveryTarget(targets: ConformityV2Target[]): Promise<{
  evaluations: ConformityV2Evaluation[] | null
  refusal: string | null
}> {
  const settled = await Promise.allSettled(
    targets.map(previewConformityV2Request),
  )
  const refusal =
    settled
      .map((r) =>
        r.status === "rejected" ? regulationPickRefusal(r.reason) : null,
      )
      .find((message) => message !== null) ?? null
  const evaluations = settled.every((r) => r.status === "fulfilled")
    ? settled.map(
        (r) => (r as PromiseFulfilledResult<ConformityV2Evaluation>).value,
      )
    : null
  return { evaluations, refusal }
}

/** Articles with no verdict data behind the whole request, counted once each.
 *  The evaluation reports them per framework and the same article blocks
 *  several, so the dialog would otherwise multiply one missing part by the
 *  number of regulations it holds up. */
function gapArticleCount(evaluations: ConformityV2Evaluation[]): number {
  const articleIds = new Set<string>()
  for (const evaluation of evaluations) {
    for (const framework of evaluation.frameworks) {
      for (const gap of framework.gaps) {
        articleIds.add(gap.articleId)
      }
    }
  }
  return articleIds.size
}
// ─── Master-data import ───────────────────────────────────────────────────────

/**
 * One import file, from the same history list the import screen renders.
 *
 * Read fresh rather than trusted from the model's input: the card has to name
 * the file the user is actually discarding, and `run` re-reads it because a
 * commit can start while the dialog is open.
 */
async function importFile(
  deps: ToolExecutorDeps,
  importId: string,
): Promise<ImportHistoryItem | undefined> {
  const history = await deps.queryClient.fetchQuery(importHistoryQuery)
  return history.find((row) => row.id === importId)
}

/** Why this file cannot be discarded, or null if it can. */
function cancelImportRefusal(file: ImportHistoryItem): string | null {
  if (file.status === "committed") {
    return (
      `"${file.filename}" has already been committed — its records are live in master data. ` +
      "Cancelling only applies before a commit, and there is no rollback: correcting committed " +
      "data means importing a corrected file or editing the records."
    )
  }
  if (file.status === "committing") {
    return (
      `"${file.filename}" is being committed right now. It cannot be discarded mid-write — wait ` +
      "for the commit to finish, then read it again to see whether it landed."
    )
  }
  return null
}

/** The model's close reason, trimmed; empty when it gave none. */
function closeReason(input: Record<string, unknown>): string {
  return typeof input.reason === "string" ? input.reason.trim() : ""
}

/** A reason is saved as an inquiry note: it needs notes on and the right to write one. */
function keepsCloseReason(reason: string, deps: ToolExecutorDeps): boolean {
  return reason !== "" && getFlagValue("MC_NOTES") && deps.canWriteInquiryNotes
}

/**
 * Close-reason notes whose close then failed, keyed by inquiry and reason. The
 * model retries a failed call, and the note is audited under the user's name,
 * so a retry closes with the note that is already saved instead of writing a
 * second one. Cleared once the close succeeds.
 */
const closeReasonNotes = new Map<string, string>()

const HANDLERS: Record<string, ToolHandler> = {
  // Files are staged on attach (see AgentChatContext.attachFile), not by a
  // tool — this is the only tool call in the file-handling family that needs
  // frontend execution, because filing into a module runs against the USER's
  // own token/permissions, not the chat's. read_file and classify_file are
  // plain SERVER tools and never reach this executor. Replaces the old
  // upload_held_file (browser-memory File + knowledge_base/questionnaire
  // only) now that the backend registers route_file/read_file/classify_file
  // instead — upload_held_file is no longer sent.
  route_file: {
    confirm: (toolCall, deps) => {
      const destination = String(toolCall.input.destination ?? "")
      const file = deps.stagedFiles.find(
        (f) => f.id === String(toolCall.input.file_id ?? ""),
      )
      return {
        title: deps.translate("AI_CHAT/ROUTE_FILE_CONFIRM_TITLE"),
        detail: deps.translate("AI_CHAT/ROUTE_FILE_CONFIRM_DETAIL", {
          fileName:
            file?.fileName ?? deps.translate("AI_CHAT/TARGET_LABEL_THE_FILE"),
          // destination is the wire slug ("supplier_declaration") — shown to
          // the user via its display name, never raw.
          destination: destinationDisplayName(destination, deps.translate),
        }),
      }
    },
    run: async (toolCall, deps) => {
      const fileId = String(toolCall.input.file_id ?? "")
      const destination = String(toolCall.input.destination ?? "")
      if (!deps.conversationId) {
        return { error: "There is no active conversation to file this from." }
      }
      if (!fileId || !destination) {
        return { error: "Missing file_id or destination." }
      }
      const result = await chatFilesApiCalls.routeFile(
        deps.conversationId,
        fileId,
        destination,
      )
      deps.onFileRouted(fileId, result.destination)
      return {
        status: "ok",
        destination: result.destination,
        ref: result.ref,
        final_file_name: result.finalFileName,
        renamed: result.renamed,
        message: result.message,
      }
    },
  },

  update_inquiry_draft: {
    confirm: (toolCall, deps) => {
      const { text, count } = changeLines(
        inquiryDraftEntries(toolCall.input, deps.translate),
        deps.translate,
      )
      if (count === 0) {
        return null
      }
      return {
        title: deps.translate("AI_CHAT/CONFIRM_INQUIRY_UPDATE_TITLE"),
        detail: `${deps.translate("AI_CHAT/CONFIRM_INQUIRY_UPDATE_DETAIL")}\n\n${text}`,
      }
    },
    run: async (toolCall, deps) => {
      const { inquiry_id: inquiryId, ...rest } = toolCall.input
      const regulations = regulationKeysInput(rest.regulation_keys)
      if (regulations.error) {
        return { error: regulations.error }
      }
      const changes = toDraftChanges(rest)
      if (Object.keys(changes).length === 0) {
        return {
          error:
            "No fields to change were given. Name at least one, e.g. email_body.",
        }
      }
      if (changes.dueAt !== undefined) {
        // Converted the way the picker does, so chat and the screen agree.
        const due = dueDate(changes.dueAt)
        if (!due) {
          return { error: unreadableDateError(changes.dueAt) }
        }
        if (due < todayIso()) {
          return { error: pastDeadlineError(due) }
        }
        changes.dueAt = isoDateToUtcMidnight(due)
      }
      const id = await resolveInquiryId(String(inquiryId))
      const detail = await patchInquiryRequest(id, changes)
      // Until this runs every mounted panel still shows the old value.
      syncPatchedInquiryCaches(deps.queryClient, id, detail)
      // The endpoint ACCEPTS the draft-only fields on an already-sent inquiry and
      // then ignores them — a 200 with nothing changed. Reporting the request as
      // the outcome would tell the user their email was rewritten when the stored
      // copy never moved, so the outcome is derived from the returned status.
      const requested = Object.keys(changes)
      const ignored =
        detail.status === "draft"
          ? []
          : requested.filter((field) => DRAFT_ONLY_CHANGES.includes(field))
      const applied = requested.filter((field) => !ignored.includes(field))
      if (applied.length) {
        // The user still has to look at it and press Send.
        const remindersOnly = applied.every((field) =>
          REMINDER_STAGE_CHANGES.includes(field),
        )
        offerNavigation(
          deps,
          inquiryDraftPath(detail.id, remindersOnly ? "reminder" : undefined),
          deps.translate("AI_CHAT/TARGET_LABEL_THIS_INQUIRY"),
        )
      }
      // A regulation or format change: say what the draft now asks, and if it is blocked.
      const scopeTouched = [
        "regulationKeys",
        "followOrgRegulations",
        "attachmentFormat",
      ].some((field) => applied.includes(field))
      const report = scopeTouched ? draftRegulationReport([detail]) : null
      return {
        status: applied.length ? "updated" : "no_change",
        inquiry_id: detail.id,
        reference: detail.reference,
        inquiry_status: detail.status,
        changed: applied,
        ignored,
        ...(report
          ? {
              attachment_format: report.drafts[0].attachment_format,
              asked_regulations: report.drafts[0].asked_regulations,
              send_blocked: report.drafts[0].send_blocked,
            }
          : {}),
        note:
          (report?.note ?? "") +
          (ignored.length
            ? `Not applied, because this inquiry is already sent (${detail.status}): ${ignored.join(", ")}. ` +
              "Tell the user those did not change and why — do not claim otherwise."
            : "Nothing has been sent — this only changed the draft."),
      }
    },
  },

  set_inquiry_auto_reminders: {
    confirm: (toolCall, deps) => ({
      title: deps.translate("AI_CHAT/CONFIRM_INQUIRY_REMINDERS_TITLE"),
      detail: deps.translate("AI_CHAT/CONFIRM_INQUIRY_REMINDERS_DETAIL", {
        mode: String(toolCall.input.mode ?? ""),
      }),
    }),
    run: async (toolCall, deps) => {
      const mode = String(toolCall.input.mode ?? "") as AutoReminderMode
      // "on" exists on the endpoint but on no screen: it would pin this inquiry
      // against a later org-wide change.
      if (mode !== "inherit" && mode !== "off") {
        return {
          error: `Unknown mode ${mode}. Use "off" to pause or "inherit" to resume.`,
        }
      }
      const detail = await setAutoRemindersDirect(
        await resolveInquiryId(String(toolCall.input.inquiry_id)),
        mode,
      )
      invalidateInquiryCaches(deps.queryClient, detail.id)
      offerNavigation(
        deps,
        INQUIRY_BASE,
        deps.translate("AI_CHAT/TARGET_LABEL_INQUIRIES"),
      )
      return {
        status: "updated",
        inquiry_id: detail.id,
        auto_reminders_override: detail.autoRemindersEnabled ?? null,
        auto_reminders_effective: detail.autoRemindersEffective,
        note: "Applies to this one inquiry only, not the org-wide cadence.",
      }
    },
  },

  create_supplier_inquiries: {
    confirm: (toolCall, deps) => {
      const recipients = supplierScopes(toolCall.input)
      if (recipients.length === 0) {
        return null
      }
      const narrowed = recipients.filter((r) => r.articleIds?.length).length
      return {
        title: deps.translate("AI_CHAT/CONFIRM_INQUIRY_CREATE_TITLE", {
          count: recipients.length,
        }),
        detail: `${deps.translate("AI_CHAT/CONFIRM_INQUIRY_CREATE_DETAIL", {
          count: recipients.length,
        })}\n\n${
          changeLines(
            [
              [
                deps.translate("AI_CHAT/FIELD_ARTICLES"),
                narrowed
                  ? deps.translate("AI_CHAT/VALUE_ARTICLES_NARROWED", {
                      narrowed,
                      total: recipients.length,
                    })
                  : deps.translate("AI_CHAT/VALUE_ARTICLES_ALL"),
              ],
              [
                deps.translate("AI_CHAT/FIELD_DEADLINE"),
                dueDate(toolCall.input.due_at) ?? undefined,
              ],
              [
                deps.translate("AI_CHAT/FIELD_LANGUAGE"),
                toolCall.input.language,
              ],
              [
                deps.translate("MC_INQ_FRAMEWORKS_LABEL"),
                toolCall.input.regulation_keys,
              ],
            ],
            deps.translate,
          ).text
        }`,
      }
    },
    run: async (toolCall, deps) => {
      const recipients = supplierScopes(toolCall.input)
      if (recipients.length === 0) {
        return {
          error:
            'Missing "suppliers". Pass at least one entry with a supplier_id.',
        }
      }
      const due = dueDate(toolCall.input.due_at)
      // A supplied-but-unreadable date must not fall through as "no deadline
      // asked for": the drafts would be created on the default window and
      // reported as done.
      if (isSupplied(toolCall.input.due_at) && !due) {
        return { error: unreadableDateError(toolCall.input.due_at) }
      }
      if (due && due < todayIso()) {
        return { error: pastDeadlineError(due) }
      }
      const regulations = regulationKeysInput(toolCall.input.regulation_keys)
      if (regulations.error) {
        return { error: regulations.error }
      }
      // The legacy labels never drive the documents; send them all, the way
      // NewInquiryPage does. What is asked is `regulationKeys` (TUR-1730).
      const frameworks = (
        await deps.queryClient.fetchQuery(inquiryFrameworksQuery)
      ).map((framework) => framework.value)
      // `recipients` only when narrowed; the supplierIds path keeps the "single"
      // scope a one-supplier create sends from the picker.
      const narrowed = recipients.some((r) => r.articleIds?.length)
      const language = toolCall.input.language as "en" | "de" | undefined
      const created = await createInquiriesRequest(
        {
          ...(narrowed
            ? { recipients }
            : { supplierIds: recipients.map((r) => r.supplierId) }),
          frameworks,
          ...(regulations.keys ? { regulationKeys: regulations.keys } : {}),
          language,
        },
        // Reads the org's document language only when the model names none.
        await newInquiryLanguage(deps.queryClient, deps.uiLanguage, language),
      )
      // Without this the composer's per-draft GET can 404 under replica lag.
      const ids = seedInquiryDraftCaches(deps.queryClient, created)
      const deadline = due
        ? await applyDeadline(deps.queryClient, ids, due)
        : null
      offerNavigation(
        deps,
        inquiryDraftPath(ids),
        ids.length > 1
          ? deps.translate("AI_CHAT/TARGET_LABEL_NEW_DRAFTS")
          : deps.translate("AI_CHAT/TARGET_LABEL_NEW_DRAFT"),
      )
      const scope = resolvedScope(created, narrowed)
      // What the drafts actually ask, read off the created rows: no keys follow the
      // org's Settings only while regulation settings are on; off, they ask the
      // full documents of their format.
      const regulationScope =
        regulations.keys ??
        (created.every((d) => d.scopeSource === "default")
          ? "org_settings"
          : "full_documents")
      const report = draftRegulationReport(created)
      return {
        status: "drafts_created",
        // The backend fans out per RESOLVED supplier, which need not match the
        // ids passed in, so the count comes from the response.
        created_count: ids.length,
        inquiry_ids: ids,
        // What the drafts ask: the keys named, the org's Settings (live until send),
        // or the full documents while regulation settings are off.
        regulations: regulationScope,
        // Per draft, what its format really asks of that and whether Send is blocked.
        drafts: report.drafts,
        ...(deadline ?? {}),
        ...scope,
        note:
          "Drafts only — nothing has been sent. The user now has a button into the composer, " +
          "where one press of Send dispatches the whole batch; tell them that is the remaining " +
          "step. " +
          report.note +
          (regulations.keys
            ? "Each draft asks only what its format covers of the regulations named (asked_regulations per draft). "
            : regulationScope === "org_settings"
              ? "The regulations follow the org's Settings > Regulations. "
              : "Regulation settings are off for this org, so each draft asks the full documents of its format. ") +
          (narrowed
            ? " Name the articles each supplier is being asked about back to the user: an article " +
              "id resolved from the wrong supplier is not refused by the endpoint, so this echo is " +
              "where a mistake becomes visible while the drafts are still unsent."
            : "") +
          (scope.unresolved_articles
            ? " SOME IDS DID NOT RESOLVE to an article in this organization and are on the " +
              "questionnaire as blank rows. Say so, and offer to fix the scope with " +
              "update_inquiry_draft."
            : ""),
      }
    },
  },

  close_inquiry: {
    confirm: (toolCall, deps) => {
      const reason = closeReason(toolCall.input)
      const detail = deps.translate("AI_CHAT/CONFIRM_INQUIRY_CLOSE_DETAIL")
      return {
        title: deps.translate("AI_CHAT/CONFIRM_INQUIRY_CLOSE_TITLE"),
        // The reason is saved as a note under the user's name, so it is shown
        // here, but only when it will be saved: with notes off, or without the
        // right to write one, it is dropped, and listing it would promise a
        // record that is never written.
        detail: keepsCloseReason(reason, deps)
          ? `${detail}\n\n${
              changeLines(
                [[deps.translate("AI_CHAT/FIELD_YOUR_NOTE"), reason]],
                deps.translate,
              ).text
            }`
          : detail,
      }
    },
    run: async (toolCall, deps) => {
      const id = await resolveInquiryId(String(toolCall.input.inquiry_id))
      // The endpoint closes a draft happily; no screen does, because a draft is
      // DISCARDED. Closing one puts a never-sent inquiry in the closed bucket.
      const current = await fetchInquiryDetail(id)
      if (current.status === "draft") {
        return {
          error:
            "This is still a draft, so there is nothing to stop — a draft is discarded, not " +
            "closed. Chat cannot discard one: use highlight_ui to show the inquiry list, where " +
            "the draft's menu has Discard.",
        }
      }
      // A reason is kept as an inquiry note, written before the close names it, the
      // same two steps the close dialog takes. Only with notes on and the right to
      // write one: the routes refuse otherwise, so without them the close goes
      // ahead and says the reason was lost.
      const reason = closeReason(toolCall.input)
      const keepReason = keepsCloseReason(reason, deps)
      const memoKey = `${id}\n${reason}`
      let noteId = keepReason ? closeReasonNotes.get(memoKey) : undefined
      let detail: Awaited<ReturnType<typeof closeInquiryDirect>>
      try {
        if (keepReason && !noteId) {
          noteId = (
            await createNoteRequest({
              entityType: "inquiry",
              entityId: id,
              body: reason,
            })
          ).id
          closeReasonNotes.set(memoKey, noteId)
        }
        detail = noteId
          ? await closeInquiryDirect(id, { noteId })
          : await closeInquiryDirect(id)
        closeReasonNotes.delete(memoKey)
      } finally {
        // The note exists even when the close failed, so the notes list must
        // show it either way.
        if (noteId) invalidateNoteCaches(deps.queryClient)
      }
      invalidateInquiryCaches(deps.queryClient, detail.id)
      offerNavigation(
        deps,
        INQUIRY_BASE,
        deps.translate("AI_CHAT/TARGET_LABEL_INQUIRIES"),
      )
      return {
        status: "closed",
        inquiry_id: detail.id,
        inquiry_status: detail.status,
        note:
          "Closed WITHOUT an answer, a deliberate stop. Do not report it as answered." +
          (reason && !keepReason
            ? getFlagValue("MC_NOTES")
              ? " The reason was NOT saved: this user may not write inquiry notes."
              : " The reason was NOT saved: notes are not enabled for this organization."
            : ""),
      }
    },
  },

  remap_declaration_article: {
    confirm: (toolCall, deps) => {
      const mappings = articleMappings(toolCall.input)
      if (mappings.length === 0) {
        return null
      }
      const lines = mappings
        .map((m) => `• ${m.supplierCode} → ${m.articleNumber}`)
        .join("\n")
      return {
        title: deps.translate("AI_CHAT/CONFIRM_DECL_REMAP_TITLE", {
          count: mappings.length,
        }),
        detail: `${deps.translate("AI_CHAT/CONFIRM_DECL_REMAP_DETAIL")}\n\n${lines}`,
      }
    },
    run: async (toolCall, deps) => {
      const id = await declarationId(toolCall.input)
      const mappings = articleMappings(toolCall.input)
      if (!id) {
        return { error: 'Missing "declaration_id".' }
      }
      if (mappings.length === 0) {
        return {
          error:
            'Missing "mappings". Pass at least one {supplier_code, article_number} pair.',
        }
      }
      const subject = await fetchDeclarationSubject(id)
      const closed = reviewClosedError(subject)
      if (closed) {
        return { error: closed }
      }
      // Said once here rather than N times from the endpoint, which refuses every
      // mapping on its own with the same reason.
      if (!subject.supplierId) {
        return { error: NO_SUPPLIER_ERROR }
      }
      // One request per pair: the endpoint takes one, and each learns its own
      // alias, so a refusal is per mapping rather than for the batch.
      const outcomes = await Promise.allSettled(
        mappings.map((m) =>
          remapArticleRequest(id, m.supplierCode, m.articleNumber),
        ),
      )
      const mapped: ArticleMapping[] = []
      const failed: { supplier_code: string; reason: string }[] = []
      outcomes.forEach((outcome, index) => {
        if (outcome.status === "fulfilled") {
          mapped.push(mappings[index])
        } else {
          failed.push({
            supplier_code: mappings[index].supplierCode,
            reason:
              apiMessage(outcome.reason) ?? "The mapping failed in the app.",
          })
        }
      })
      if (mapped.length) {
        invalidateDeclaration(deps.queryClient, id)
        // The declaration's review, with no statement addressed: a remap is not
        // scoped to one statement, so there is no card to address.
        offerNavigation(
          deps,
          declarationReviewPath(id),
          deps.translate("AI_CHAT/TARGET_LABEL_THIS_DECLARATION"),
        )
      }
      return {
        // Never one collective verdict: a partly-refused batch reported as mapped
        // would tell the user a part number is resolved when it is not.
        status:
          failed.length === 0
            ? "mapped"
            : mapped.length
              ? "partially_mapped"
              : "not_mapped",
        mapped: mapped.map((m) => `${m.supplierCode} → ${m.articleNumber}`),
        failed,
        note:
          (mapped.length
            ? `${mapped.length} mapping(s) applied, and each is now the learned default for this ` +
              "supplier's future declarations. "
            : "Nothing was mapped. ") +
          (failed.length
            ? "Report the failures individually with their reasons — an article number we do not " +
              "hold is refused rather than dropped."
            : "The coverage is re-derived from them, so read get_declaration_review again before " +
              "saying what is left."),
      }
    },
  },

  record_declaration_override: {
    confirm: async (toolCall, deps) => {
      const kind = overrideKind(toolCall.input.kind)
      const comment = str(toolCall.input.comment)
      const id = await declarationId(toolCall.input)
      const statementId = str(toolCall.input.statement_id)
      if (!kind || !comment || !id || !statementId) {
        // `run` refuses these with the reason; a dialog first would ask the user
        // to approve a call that cannot happen.
        return null
      }
      const target = await describeOverrideTarget(
        id,
        statementId,
        str(toolCall.input.article_number),
        deps.translate,
      )
      const { text } = changeLines(
        [
          [deps.translate("AI_CHAT/FIELD_ITEM"), target],
          [
            deps.translate("AI_CHAT/FIELD_PARKED_AS"),
            deps.translate(OVERRIDE_KIND_SENTENCE_KEYS[kind]),
          ],
          [deps.translate("AI_CHAT/FIELD_YOUR_NOTE"), comment],
        ],
        deps.translate,
      )
      return {
        title: deps.translate("AI_CHAT/CONFIRM_DECL_OVERRIDE_TITLE"),
        detail: `${deps.translate("AI_CHAT/CONFIRM_DECL_OVERRIDE_DETAIL")}\n\n${text}`,
      }
    },
    run: async (toolCall, deps) => {
      const id = await declarationId(toolCall.input)
      const statementId = str(toolCall.input.statement_id)
      const kind = overrideKind(toolCall.input.kind)
      const comment = str(toolCall.input.comment)
      const articleNumber = str(toolCall.input.article_number)
      if (!id || !statementId) {
        return { error: 'Missing "declaration_id" or "statement_id".' }
      }
      if (!kind) {
        return {
          error:
            `Unknown override kind ${String(toolCall.input.kind)}. Use one of: ` +
            `${OVERRIDE_KINDS.join(", ")}.`,
        }
      }
      if (!comment) {
        // The server refuses a blank note too; saying it here keeps the user from
        // approving a dialog that cannot succeed.
        return {
          error:
            "An override needs a note — there is no document quote behind one, so the note is " +
            "the record of why. Ask the user for their reason rather than writing one for them.",
        }
      }
      const subject = await fetchDeclarationSubject(id)
      const closed = reviewClosedError(subject)
      if (closed) {
        return { error: closed }
      }
      const facts = locateStatement(
        await fetchDeclarationStatements(id),
        statementId,
      )
      if (!facts) {
        return { error: UNKNOWN_STATEMENT_ERROR }
      }
      const refusal = await overrideRefusal(
        id,
        kind,
        facts,
        articleNumber,
        subject.supplierId,
      )
      if (refusal) {
        return { error: refusal }
      }
      await recordOverrideRequest(id, {
        address: overrideAddressFor(facts, articleNumber),
        kind,
        comment,
      })
      // EXCLUSION IS TWO WRITES, and the note is only the first. The staged row's
      // `rejected` status is the exclusion itself — it is what takes the statement
      // out of what approve writes, and the one fact both sides read to decide the
      // disposition is in force. Recording the note alone leaves a saved reason
      // over a statement still pending, which clears nothing.
      if (kind === "excluded") {
        try {
          await reviewStatementRequest(statementId, "reject")
        } catch (error) {
          // The note stands and the statement does not: said plainly, because
          // the reviewer would otherwise believe the exclusion took.
          invalidateDeclaration(deps.queryClient, id)
          return {
            status: "partially_applied",
            note_recorded: true,
            statement_rejected: false,
            error:
              "The note was saved but the statement was NOT excluded: " +
              `${apiMessage(error) ?? "the rejection failed in the app."} The statement is still ` +
              "in the approval, so tell the user the exclusion did not take effect — a note on " +
              "its own closes nothing.",
          }
        }
      }
      invalidateDeclaration(deps.queryClient, id)
      offerNavigation(
        deps,
        declarationReviewPath(id, { statementId }),
        deps.translate("AI_CHAT/TARGET_LABEL_PARKED_ITEM"),
      )
      return {
        status: "parked",
        declaration_id: id,
        statement_id: statementId,
        kind,
        ...(articleNumber ? { article_number: articleNumber } : {}),
        ...(kind === "excluded" ? { statement_rejected: true } : {}),
        note:
          "This is a disposition, not a value: no verdict, coverage or substance value changed. " +
          "It stops that item blocking the approval, and the note is stored under the user's name. " +
          (kind === "proceed_unknown"
            ? "It retires by itself the moment a real verdict is set. "
            : "retire_declaration_override hands it back. ") +
          // Parking an item is valid with no supplier — it is a per-statement
          // disposition, not a master-data write. But the declaration still
          // cannot be approved, and a result that says only "stops that item
          // blocking the approval" reads as though it now can.
          (subject.supplierId
            ? ""
            : "THE DECLARATION STILL CANNOT BE APPROVED: it has no master-data supplier, which blocks " +
              "approval on its own however every statement is dispositioned. Say that, and offer " +
              "assign_declaration_supplier."),
      }
    },
  },

  retire_declaration_override: {
    confirm: async (toolCall, deps) => {
      const id = await declarationId(toolCall.input)
      const statementId = str(toolCall.input.statement_id)
      if (!id || !statementId) {
        return null
      }
      const target = await describeOverrideTarget(
        id,
        statementId,
        str(toolCall.input.article_number),
        deps.translate,
      )
      return {
        title: deps.translate("AI_CHAT/CONFIRM_DECL_OVERRIDE_RETIRE_TITLE"),
        detail: `${deps.translate(
          "AI_CHAT/CONFIRM_DECL_OVERRIDE_RETIRE_DETAIL",
        )}\n\n${
          changeLines(
            [[deps.translate("AI_CHAT/FIELD_ITEM"), target]],
            deps.translate,
          ).text
        }`,
      }
    },
    run: async (toolCall, deps) => {
      const id = await declarationId(toolCall.input)
      const statementId = str(toolCall.input.statement_id)
      const articleNumber = str(toolCall.input.article_number)
      if (!id || !statementId) {
        return { error: 'Missing "declaration_id" or "statement_id".' }
      }
      const closed = reviewClosedError(await fetchDeclarationSubject(id))
      if (closed) {
        return { error: closed }
      }
      const facts = locateStatement(
        await fetchDeclarationStatements(id),
        statementId,
      )
      if (!facts) {
        return { error: UNKNOWN_STATEMENT_ERROR }
      }
      const address = overrideAddressFor(facts, articleNumber)
      // Retiring is idempotent, so the call cannot say whether anything was
      // parked. Reading first is what keeps "handed it back" from being said
      // about an address that never held a note.
      const standing = articleNumber
        ? await noteAt(id, address)
        : facts.overrideKind
      await retireOverrideRequest(id, address)
      invalidateDeclaration(deps.queryClient, id)
      if (standing) {
        offerNavigation(
          deps,
          declarationReviewPath(id, { statementId }),
          deps.translate("AI_CHAT/TARGET_LABEL_REOPENED_ITEM"),
        )
      }
      return {
        status: standing ? "handed_back" : "nothing_was_parked",
        declaration_id: id,
        statement_id: statementId,
        ...(articleNumber ? { article_number: articleNumber } : {}),
        was_parked: standing ?? null,
        note: standing
          ? `The "${standing}" note is retired, not deleted — it stays on the record with its ` +
            "author. The item is open work in the review again."
          : "Nothing was parked at that address, so nothing changed. Say that rather than " +
            "reporting a hand-back.",
      }
    },
  },

  assign_declaration_supplier: {
    confirm: async (toolCall, deps) => {
      const id = await declarationId(toolCall.input)
      if (!id) {
        // `run` refuses this with the reason; a dialog first would ask the user
        // to approve a call that cannot happen.
        return null
      }
      const { text } = changeLines(
        [
          [
            deps.translate("AI_CHAT/FIELD_DECLARATION"),
            await describeDeclaration(id),
          ],
          [
            deps.translate("AI_CHAT/FIELD_NEW_SUPPLIER"),
            str(toolCall.input.supplier_id),
          ],
        ],
        deps.translate,
      )
      return {
        title: deps.translate("AI_CHAT/CONFIRM_DECL_SUPPLIER_TITLE"),
        detail: `${deps.translate("AI_CHAT/CONFIRM_DECL_SUPPLIER_DETAIL")}\n\n${text}`,
      }
    },
    run: async (toolCall, deps) => {
      const id = await declarationId(toolCall.input)
      const supplierId = str(toolCall.input.supplier_id)
      if (!id || !supplierId) {
        return { error: 'Missing "declaration_id" or "supplier_id".' }
      }
      const subject = await fetchDeclarationSubject(id)
      if (subject.approvedAt) {
        return {
          error:
            "This declaration is already approved — its values are in master data against the " +
            "supplier it was approved under, and reassigning it now would not move them.",
        }
      }
      await assignDeclarationSupplierRequest(id, supplierId)
      invalidateDeclaration(deps.queryClient, id)
      // The row itself carries the supplier name, so the list and tiles move too.
      invalidateDeclarationRows(deps.queryClient)
      offerNavigation(
        deps,
        declarationReviewPath(id),
        deps.translate("AI_CHAT/TARGET_LABEL_THIS_DECLARATION"),
      )
      return {
        status: "supplier_assigned",
        declaration_id: id,
        supplier_id: supplierId,
        previous_supplier_id: subject.supplierId,
        note:
          "Only the supplier changed. The article coverage is now re-evaluated against that " +
          "supplier's own catalogue, so unmatched part numbers can appear or disappear — read " +
          "get_declaration_scope again rather than assuming the coverage survived. Name the " +
          "supplier back to the user.",
      }
    },
  },

  retry_declaration_extraction: {
    // Same reasoning as assign: re-reading the wrong document is a mis-pick the
    // dialog should make visible.
    confirm: async (toolCall, deps) => {
      const id = await declarationId(toolCall.input)
      if (!id) {
        return null
      }
      const { text } = changeLines(
        [
          [
            deps.translate("AI_CHAT/FIELD_DECLARATION"),
            await describeDeclaration(id),
          ],
        ],
        deps.translate,
      )
      return {
        title: deps.translate("AI_CHAT/CONFIRM_DECL_RETRY_TITLE"),
        detail: `${deps.translate("AI_CHAT/CONFIRM_DECL_RETRY_DETAIL")}\n\n${text}`,
      }
    },
    run: async (toolCall, deps) => {
      const id = await declarationId(toolCall.input)
      if (!id) {
        return { error: 'Missing "declaration_id".' }
      }
      const subject = await fetchDeclarationSubject(id)
      // The endpoint retries anything not actively running; the inbox offers the
      // control on a FAILED row only. Re-reading a document a reviewer has already
      // worked on would move the ground under them.
      if (subject.lifecycleStatus !== "failed") {
        return {
          error:
            `This declaration is "${subject.lifecycleStatus ?? subject.processingStatus}", not ` +
            "failed, and only a failed extraction is re-run — that is the one state the app " +
            "offers it in. One still processing is already running, and one that came through " +
            "is reviewed rather than re-read.",
        }
      }
      await retryDeclarationRequest(id)
      invalidateDeclarationRows(deps.queryClient)
      offerNavigation(
        deps,
        DECLARATIONS_BASE,
        deps.translate("AI_CHAT/TARGET_LABEL_YOUR_DECLARATIONS"),
      )
      return {
        status: "extraction_queued",
        declaration_id: id,
        note:
          "The document is queued to be read again — extraction is IN FLIGHT, not finished, so " +
          "do not report the declaration as extracted or reviewable yet. It appears in the " +
          "review once the run lands.",
      }
    },
  },

  save_inquiry_template_settings: {
    confirm: (toolCall, deps) => {
      const { text, count } = changeLines(
        templateSettingsEntries(toolCall.input, deps.translate),
        deps.translate,
      )
      if (count === 0) {
        return null
      }
      return {
        title: deps.translate("AI_CHAT/CONFIRM_INQUIRY_SETTINGS_TITLE"),
        detail: `${deps.translate("AI_CHAT/CONFIRM_INQUIRY_SETTINGS_DETAIL")}\n\n${text}`,
      }
    },
    run: async (toolCall, deps) => {
      const patch = toTemplateSettingsPatch(toolCall.input)
      if (Object.keys(patch).length === 0) {
        return {
          error:
            "No settings to change were given. Name at least one, e.g. reminder_interval_days.",
        }
      }
      // saveTemplateSettings re-reads and merges: the endpoint is a full upsert
      // that would otherwise blank the footer, contact person and templates.
      const saved = await saveTemplateSettings(patch)
      // Until this runs the settings pages keep showing the pre-save values.
      syncTemplateSettingsCache(deps.queryClient, saved)
      const changed = Object.keys(patch)
      offerNavigation(
        deps,
        settingsPathFor(changed),
        deps.translate("AI_CHAT/TARGET_LABEL_INQUIRY_TEMPLATE_SETTINGS"),
      )
      const ignoredSubjects = reminderSubjectCells(toolCall.input)
      return {
        status: "saved",
        changed,
        reminders_enabled: saved.remindersEnabled,
        reminder_interval_days: saved.reminderIntervalDays,
        max_reminders: saved.maxReminders,
        ...(ignoredSubjects.length
          ? {
              ignored: ignoredSubjects.map(
                (lang) => `reminder subject (${lang})`,
              ),
            }
          : {}),
        note:
          "This applies to every future inquiry, not just one. " +
          (ignoredSubjects.length
            ? "The reminder SUBJECT was not changed: a reminder threads onto the original email, " +
              'so its outbound subject is always "Re: " plus the first subject. Tell the user ' +
              "that specifically — the body did change."
            : ""),
      }
    },
  },

  update_supplier: masterDataFieldHandler({
    entity: "supplier",
    fields: [
      ["supplier_name", "name", "MC_MD_FIELD_NAME"],
      ["email", "email", "MC_MD_FIELD_EMAIL"],
      ["country", "country", "MC_MD_FIELD_COUNTRY"],
      ["duns_number", "dunsNumber", "MC_MD_FIELD_DUNS"],
      [
        "declaration_validity_days",
        "declarationValidityDays",
        "MC_MD_FIELD_DECLARATION_VALIDITY",
      ],
    ],
    consequenceKeys: {
      declaration_validity_days: "AI_CHAT/CONFIRM_MD_VALIDITY_NOTE",
    },
  }),

  update_article: masterDataFieldHandler({
    entity: "article",
    fields: [
      ["article_name", "name", "MC_MD_FIELD_NAME"],
      ["material_group", "materialGroup", "MC_MD_FIELD_MATERIAL_GROUP"],
      ["product_group", "productGroup", "MC_MD_FIELD_PRODUCT_GROUP"],
      ["country_of_origin", "countryOfOrigin", "MC_MD_FIELD_COUNTRY_OF_ORIGIN"],
      ["manufacturer_name", "manufacturerName", "MC_MD_FIELD_MANUFACTURER"],
    ],
  }),

  update_product: masterDataFieldHandler({
    entity: "product",
    fields: [
      ["product_name", "name", "MC_MD_FIELD_NAME"],
      ["status", "status", "MC_MD_FIELD_STATUS"],
      ["revision", "revision", "MC_MD_FIELD_REVISION"],
    ],
  }),

  update_customer: masterDataFieldHandler({
    entity: "customer",
    fields: [
      ["customer_name", "name", "MC_MD_FIELD_NAME"],
      ["email", "email", "MC_MD_FIELD_EMAIL"],
      [
        "declaration_freshness_days",
        "declarationFreshnessDays",
        "MC_MD_FIELD_DECLARATION_FRESHNESS",
      ],
      [
        "concentration_threshold_pct",
        "concentrationThresholdPct",
        "MC_MD_FIELD_CONCENTRATION_THRESHOLD",
      ],
      [
        "preferred_response_format",
        "preferredResponseFormat",
        "MC_MD_FIELD_PREFERRED_RESPONSE_FORMAT",
      ],
    ],
    consequenceKeys: {
      concentration_threshold_pct: "AI_CHAT/CONFIRM_MD_THRESHOLD_NOTE",
    },
  }),

  create_customer: {
    confirm: (toolCall, deps) => {
      const name = str(toolCall.input.name)
      if (!name) return null
      const { text } = changeLines(
        [
          [deps.translate("MC_MD_FIELD_EMAIL"), str(toolCall.input.email)],
          [
            deps.translate("MC_MD_FIELD_DECLARATION_FRESHNESS"),
            toolCall.input.declaration_freshness_days,
          ],
          [
            deps.translate("MC_MD_FIELD_CONCENTRATION_THRESHOLD"),
            toolCall.input.concentration_threshold_pct,
          ],
          [
            deps.translate("MC_MD_FIELD_PREFERRED_RESPONSE_FORMAT"),
            str(toolCall.input.preferred_response_format),
          ],
        ],
        deps.translate,
      )
      const detail = deps.translate("AI_CHAT/CONFIRM_MD_CUSTOMER_CREATE_DETAIL")
      return {
        title: deps.translate("AI_CHAT/CONFIRM_MD_CUSTOMER_CREATE_TITLE", {
          record: name,
        }),
        detail: text ? `${detail}\n\n${text}` : detail,
      }
    },
    run: async (toolCall, deps) => {
      const name = str(toolCall.input.name)
      if (!name) {
        return { error: 'Missing "name" — a customer must have one.' }
      }
      const body: Record<string, unknown> = { name }
      if (isSupplied(toolCall.input.email)) body.email = toolCall.input.email
      if (isSupplied(toolCall.input.declaration_freshness_days)) {
        body.declarationFreshnessDays = Number(
          toolCall.input.declaration_freshness_days,
        )
      }
      if (isSupplied(toolCall.input.concentration_threshold_pct)) {
        body.concentrationThresholdPct = Number(
          toolCall.input.concentration_threshold_pct,
        )
      }
      if (isSupplied(toolCall.input.preferred_response_format)) {
        body.preferredResponseFormat = toolCall.input.preferred_response_format
      }
      const created = await createCustomerDirect(deps.queryClient, body)
      offerNavigation(
        deps,
        masterDataRecordRoute("customer", created.id),
        created.name,
      )
      return {
        status: "created",
        customer_id: created.id,
        name: created.name,
        // Reported from the response, not the request: an omitted threshold is
        // an inherited default, and saying "0.1" would claim a stored override.
        concentration_threshold_pct: created.concentrationThresholdPct ?? null,
        note:
          created.concentrationThresholdPct === null ||
          created.concentrationThresholdPct === undefined
            ? "No disclosure threshold is stored, so this customer inherits the 0.1% w/w REACH Article 33 default."
            : "A disclosure threshold is stored for this customer, overriding the 0.1% w/w default.",
      }
    },
  },

  link_article_suppliers: {
    confirm: async (toolCall, deps) => {
      const articleId = str(toolCall.input.record_id)
      const links = supplierLinks(toolCall.input)
      if (!articleId || links.length === 0) return null
      const articleName = await recordLabel(
        "article",
        articleId,
        toolCall.input,
      )
      const lines = links
        .map((link) =>
          link.supplierArticleNumber
            ? `• ${link.supplierName} (their part no. ${link.supplierArticleNumber})`
            : `• ${link.supplierName}`,
        )
        .join("\n")
      return {
        title: deps.translate("AI_CHAT/CONFIRM_MD_LINK_TITLE", {
          count: links.length,
          record: articleName,
        }),
        detail: `${deps.translate("AI_CHAT/CONFIRM_MD_LINK_DETAIL")}\n\n${lines}`,
      }
    },
    run: async (toolCall, deps) => {
      const articleId = str(toolCall.input.record_id)
      if (!articleId) {
        return { error: 'Missing "record_id" — the article to link to.' }
      }
      const links = supplierLinks(toolCall.input)
      if (links.length === 0) {
        return {
          error:
            'No suppliers were given. Pass "suppliers" with at least one supplier_id and supplier_name.',
        }
      }
      const mismatch = await verifyRecordName(
        "article",
        articleId,
        toolCall.input,
      )
      if (mismatch) return mismatch
      const supplierMismatch = await verifySupplierNames(links)
      if (supplierMismatch) return supplierMismatch
      // The upsert this calls is idempotent by design — it reactivates a
      // deactivated link rather than 409ing — so an already-linked supplier is
      // never refused. Classified against the current rows BEFORE writing, so a
      // reactivation is reported as one honestly instead of reading as a plain
      // "linked" the way a fresh link does.
      const existing = await fetchArticleSuppliers(articleId).catch(() => [])
      const existingById = new Map(existing.map((row) => [row.supplierId, row]))
      const results = await Promise.allSettled(
        links.map((link) =>
          addArticleSupplierDirect(
            deps.queryClient,
            articleId,
            link.supplierId,
            link.supplierArticleNumber,
          ),
        ),
      )
      const linked: string[] = []
      const reactivated: string[] = []
      const alreadyActive: string[] = []
      const failed: string[] = []
      results.forEach((result, index) => {
        const link = links[index]
        const name = link.supplierName
        if (result.status !== "fulfilled") {
          failed.push(`${name}: ${apiMessage(result.reason) ?? "failed"}`)
          return
        }
        const before = existingById.get(link.supplierId)
        if (!before) {
          linked.push(name)
        } else if (before.active) {
          alreadyActive.push(name)
        } else {
          reactivated.push(name)
        }
      })
      if (linked.length || reactivated.length) {
        offerNavigation(
          deps,
          masterDataRecordRoute("article", articleId),
          deps.translate("AI_CHAT/TARGET_LABEL_THIS_ARTICLE"),
        )
      }
      return {
        status: failed.length
          ? linked.length || reactivated.length
            ? "partially_linked"
            : "failed"
          : "linked",
        article_id: articleId,
        linked,
        reactivated,
        already_active: alreadyActive,
        failed,
        note:
          (failed.length
            ? "Say which suppliers were affected and why. Never report a partly-failed batch as done. "
            : "") +
          (reactivated.length
            ? `${reactivated.join(", ")} had a DEACTIVATED link that this reactivated — tell the user ` +
              "that explicitly, since they may not have intended to undo a deliberate deactivation. "
            : "") +
          (alreadyActive.length
            ? `${alreadyActive.join(", ")} were already actively linked; nothing changed for them — ` +
              "use update_article_supplier_link to change their part number. "
            : "") +
          (linked.length &&
          !failed.length &&
          !reactivated.length &&
          !alreadyActive.length
            ? "An article can have several suppliers; these were added alongside any existing ones."
            : ""),
      }
    },
  },

  update_article_supplier_link: {
    confirm: async (toolCall, deps) => {
      const { text, count } = changeLines(
        [
          [
            deps.translate("AI_CHAT/FIELD_SUPPLIER_PART_NUMBER"),
            clearableText(toolCall.input.supplier_article_number),
          ],
          [
            deps.translate("AI_CHAT/FIELD_CURRENTLY_SUPPLYING"),
            boolLabel(toolCall.input.active, deps.translate),
          ],
        ],
        deps.translate,
      )
      if (count === 0) return null
      const supplier =
        str(toolCall.input.supplier_name) ??
        deps.translate("AI_CHAT/TARGET_LABEL_THIS_SUPPLIER")
      return {
        title: deps.translate("AI_CHAT/CONFIRM_MD_LINK_UPDATE_TITLE"),
        detail: `${supplier}\n\n${text}`,
      }
    },
    run: async (toolCall, deps) => {
      const articleId = str(toolCall.input.record_id)
      const supplierId = str(toolCall.input.supplier_id)
      if (!articleId || !supplierId) {
        return {
          error:
            'Both "record_id" (the article) and "supplier_id" are needed to change a link.',
        }
      }
      const changes: Record<string, unknown> = {}
      const partNumber = clearableText(toolCall.input.supplier_article_number)
      if (partNumber !== undefined) {
        changes.supplierArticleNumber = partNumber === "" ? null : partNumber
      }
      if (isSupplied(toolCall.input.active)) {
        changes.active = Boolean(toolCall.input.active)
      }
      if (Object.keys(changes).length === 0) {
        return {
          error:
            "No changes were given. Name supplier_article_number, active, or both.",
        }
      }
      const mismatch = await verifyRecordName(
        "article",
        articleId,
        toolCall.input,
      )
      if (mismatch) return mismatch
      const supplierMismatch = await verifyRecordName("supplier", supplierId, {
        name: toolCall.input.supplier_name,
      })
      if (supplierMismatch) return supplierMismatch
      const link = await updateArticleSupplierDirect(
        deps.queryClient,
        articleId,
        supplierId,
        changes,
      )
      offerNavigation(
        deps,
        masterDataRecordRoute("article", articleId),
        deps.translate("AI_CHAT/TARGET_LABEL_THIS_ARTICLE"),
      )
      return {
        status: "updated",
        article_id: articleId,
        supplier_id: supplierId,
        supplier_name: link.supplierName,
        supplier_article_number: link.supplierArticleNumber,
        active: link.active,
        changed: Object.keys(changes),
        note: link.active
          ? "The link is current."
          : "The link is now inactive: the history is kept, and this supplier is no longer treated as supplying the article.",
      }
    },
  },

  unlink_article_supplier: {
    confirm: async (toolCall, deps) => {
      const articleId = str(toolCall.input.record_id)
      if (!articleId) return null
      const articleName = await recordLabel(
        "article",
        articleId,
        toolCall.input,
      )
      return {
        title: deps.translate("AI_CHAT/CONFIRM_MD_UNLINK_TITLE", {
          supplier:
            str(toolCall.input.supplier_name) ??
            deps.translate("AI_CHAT/TARGET_LABEL_THIS_SUPPLIER"),
          record: articleName,
        }),
        detail: deps.translate("AI_CHAT/CONFIRM_MD_UNLINK_DETAIL"),
      }
    },
    run: async (toolCall, deps) => {
      const articleId = str(toolCall.input.record_id)
      const supplierId = str(toolCall.input.supplier_id)
      if (!articleId || !supplierId) {
        return {
          error:
            'Both "record_id" (the article) and "supplier_id" are needed to remove a link.',
        }
      }
      const mismatch = await verifyRecordName(
        "article",
        articleId,
        toolCall.input,
      )
      if (mismatch) return mismatch
      const supplierMismatch = await verifyRecordName("supplier", supplierId, {
        name: toolCall.input.supplier_name,
      })
      if (supplierMismatch) return supplierMismatch
      await removeArticleSupplierDirect(deps.queryClient, articleId, supplierId)
      offerNavigation(
        deps,
        masterDataRecordRoute("article", articleId),
        deps.translate("AI_CHAT/TARGET_LABEL_THIS_ARTICLE"),
      )
      return {
        status: "unlinked",
        article_id: articleId,
        supplier_id: supplierId,
        note:
          "The link is gone, not archived. If the supplier had genuinely supplied this part and " +
          "stopped, update_article_supplier_link with active=false would have kept that history — " +
          "say so if the user may have meant that.",
      }
    },
  },

  override_article_compliance: {
    confirm: (toolCall, deps) => {
      const status = str(toolCall.input.status)
      if (!status) return null
      const exemption = str(toolCall.input.exemption_code)
      const detail = deps.translate("AI_CHAT/CONFIRM_MD_VERDICT_DETAIL")
      const { text } = changeLines(
        [
          [
            deps.translate("AI_CHAT/FIELD_NEW_VERDICT"),
            deps.translate(verdictMeta(status).labelKey),
          ],
          [deps.translate("AI_CHAT/FIELD_EXEMPTION"), exemption],
        ],
        deps.translate,
      )
      return {
        title: deps.translate("AI_CHAT/CONFIRM_MD_VERDICT_TITLE", {
          regulation:
            str(toolCall.input.regulation_name) ??
            deps.translate("AI_CHAT/TARGET_LABEL_THIS_REGULATION"),
        }),
        detail: `${detail}\n\n${text}`,
      }
    },
    run: async (toolCall, deps) => {
      const articleId = str(toolCall.input.record_id)
      const complianceId = str(toolCall.input.compliance_id)
      const status = str(toolCall.input.status)
      if (!articleId || !complianceId || !status) {
        return {
          error:
            'All of "record_id" (the article), "compliance_id" and "status" are needed for an override.',
        }
      }
      if (!VERDICT_VALUES.includes(status)) {
        return {
          error: `${status} is not a verdict this app records. Use one of: ${VERDICT_VALUES.join(", ")}.`,
        }
      }
      const mismatch = await verifyRecordName(
        "article",
        articleId,
        toolCall.input,
      )
      if (mismatch) return mismatch
      // Checked against the live rows, not the model's memory: this endpoint
      // UPDATES a verdict and cannot create one, so a stale compliance_id would
      // 404 with nothing the model can explain to the user.
      const rows = await fetchArticleCompliance(articleId)
      const row = rows.find((candidate) => candidate.id === complianceId)
      if (!row) {
        return {
          error:
            `No compliance row ${complianceId} on this article. There are ${rows.length}. ` +
            "This tool overrides an EXISTING verdict and cannot create one — if no row covers the " +
            "regulation the user means, say that: usually it means no declaration covers this " +
            "article yet, and the answer is a declaration, not an override.",
        }
      }
      // Same guard as the article-name check above, applied to the regulation:
      // a stale or misremembered compliance_id paired with the wrong claimed
      // regulation_name would otherwise override the wrong regulation's verdict
      // silently. Only refuses when both names are present and actually
      // disagree — a null regulationName (older import rows) or an omitted
      // claim isn't treated as a mismatch, since there is nothing to check.
      const claimedRegulation = str(toolCall.input.regulation_name)
      if (
        claimedRegulation &&
        row.regulationName &&
        !namesMatch(claimedRegulation, row.regulationName)
      ) {
        return {
          error:
            `compliance_id ${complianceId} covers "${row.regulationName}", not ` +
            `"${claimedRegulation}" — this is not the regulation you meant, and nothing was ` +
            "changed. Call list_article_compliance again and use the regulation_name from that " +
            "fresh result.",
        }
      }
      const saved = await overrideArticleComplianceDirect(
        deps.queryClient,
        articleId,
        complianceId,
        status,
        str(toolCall.input.exemption_code) ?? null,
      )
      offerNavigation(
        deps,
        masterDataRecordRoute("article", articleId),
        deps.translate("AI_CHAT/TARGET_LABEL_THIS_ARTICLE"),
      )
      return {
        status: "overridden",
        article_id: articleId,
        compliance_id: complianceId,
        regulation_name: saved.regulationName,
        previous_status: row.status,
        new_status: saved.status,
        source: saved.source,
        note:
          "Recorded with source=manual, which is how the app marks a verdict a person decided " +
          "rather than one a declaration reported. State what it was before, not only what it is now.",
      }
    },
  },

  answer_customer_request_gaps: {
    confirm: (toolCall, deps) => {
      const answers = answerAddresses(toolCall.input, { withValue: true })
      if (answers.length === 0) {
        return null
      }
      const lines = answers
        .slice(0, MAX_LISTED_ITEMS)
        .map((a) => `• ${a.part} · ${a.canonicalId} → ${a.value}`)
      const rest = answers.length - lines.length
      return {
        title: deps.translate("AI_CHAT/CONFIRM_CR_ANSWER_TITLE", {
          count: answers.length,
        }),
        detail: `${deps.translate("AI_CHAT/CONFIRM_CR_ANSWER_DETAIL")}\n\n${[
          ...lines,
          ...(rest > 0 ? [`• …and ${rest} more`] : []),
        ].join("\n")}`,
      }
    },
    run: async (toolCall, deps) => {
      const located = await locateAnswers(
        toolCall.input,
        answerAddresses(toolCall.input, { withValue: true }),
        { withValue: true },
      )
      if ("error" in located) {
        return located
      }
      const { requestId, found, failed } = located
      // Typing over a stale answer is not what the app offers: its card carries
      // "accept as current" instead, and the two mean different things — one
      // replaces the value, the other vouches for the one already there.
      const typeable = found.filter((hit) => {
        if (hit.gap.answerStatus === "stale") {
          failed.push({
            part: hit.address.part,
            question: hit.address.canonicalId,
            reason:
              "This answer is STALE, and the app does not type over a stale value — it offers " +
              '"accept as current" for it. Use accept_customer_request_stale_answers, or say what ' +
              "expired first.",
          })
          return false
        }
        return true
      })
      if (typeable.length === 0) {
        return {
          status: "not_answered",
          answered: [],
          failed,
          note: "Nothing was answered. Report the failures individually with their reasons.",
        }
      }
      const outcomes = await Promise.allSettled(
        typeable.map((hit) =>
          overrideAnswerRequest(
            requestId,
            hit.gap.answerId,
            hit.address.value!,
          ),
        ),
      )
      const answered: string[] = []
      outcomes.forEach((outcome, index) => {
        const hit = typeable[index]
        if (outcome.status === "fulfilled") {
          answered.push(`${hit.address.part} · ${hit.address.canonicalId}`)
        } else {
          failed.push({
            part: hit.address.part,
            question: hit.address.canonicalId,
            reason:
              apiMessage(outcome.reason) ?? "The write failed in the app.",
          })
        }
      })
      if (answered.length) {
        invalidateAnswerWrites(deps.queryClient, requestId)
        offerNavigation(
          deps,
          customerRequestProcessPath(requestId, "gaps"),
          deps.translate("AI_CHAT/TARGET_LABEL_THIS_REQUEST"),
        )
      }
      return {
        // Never one collective verdict: a partly-refused batch reported as
        // answered would tell the user a cell is filled when it is empty.
        status:
          failed.length === 0
            ? "answered"
            : answered.length
              ? "partially_answered"
              : "not_answered",
        answered,
        failed,
        note:
          (answered.length
            ? `${answered.length} answer(s) written into this response only. MASTER DATA IS UNCHANGED — ` +
              "the customer now has an answer, and the data behind it still needs fixing. "
            : "Nothing was answered. ") +
          (failed.length
            ? "Report the failures individually with their reasons."
            : "Read list_customer_request_gaps again before saying what is left."),
      }
    },
  },

  accept_customer_request_stale_answers: {
    confirm: (toolCall, deps) => {
      const answers = answerAddresses(toolCall.input, { withValue: false })
      if (answers.length === 0) {
        return null
      }
      const lines = answers
        .slice(0, MAX_LISTED_ITEMS)
        .map((a) => `• ${a.part} · ${a.canonicalId}`)
      const rest = answers.length - lines.length
      return {
        title: deps.translate("AI_CHAT/CONFIRM_CR_STALE_TITLE", {
          count: answers.length,
        }),
        detail: `${deps.translate("AI_CHAT/CONFIRM_CR_STALE_DETAIL")}\n\n${[
          ...lines,
          ...(rest > 0 ? [`• …and ${rest} more`] : []),
        ].join("\n")}`,
      }
    },
    run: async (toolCall, deps) => {
      const located = await locateAnswers(
        toolCall.input,
        answerAddresses(toolCall.input, { withValue: false }),
        { withValue: false },
      )
      if ("error" in located) {
        return located
      }
      const { requestId, found, failed } = located
      // The endpoint refuses a non-stale answer with the same rule; refusing here
      // names the row, which its 409 cannot.
      const stale = found.filter((hit) => {
        if (hit.gap.answerStatus !== "stale") {
          failed.push({
            part: hit.address.part,
            question: hit.address.canonicalId,
            reason:
              `This answer is "${hit.gap.answerStatus}", not stale, and only a stale answer can be ` +
              "accepted as current. Nothing about it has expired.",
          })
          return false
        }
        return true
      })
      if (stale.length === 0) {
        return {
          status: "not_accepted",
          accepted: [],
          failed,
          note: "Nothing was accepted. Report the failures individually with their reasons.",
        }
      }
      const outcomes = await Promise.allSettled(
        stale.map((hit) => acceptStaleRequest(requestId, hit.gap.answerId)),
      )
      const accepted: string[] = []
      outcomes.forEach((outcome, index) => {
        const hit = stale[index]
        if (outcome.status === "fulfilled") {
          accepted.push(`${hit.address.part} · ${hit.address.canonicalId}`)
        } else {
          failed.push({
            part: hit.address.part,
            question: hit.address.canonicalId,
            reason:
              apiMessage(outcome.reason) ?? "The write failed in the app.",
          })
        }
      })
      if (accepted.length) {
        invalidateAnswerWrites(deps.queryClient, requestId)
        offerNavigation(
          deps,
          customerRequestProcessPath(requestId, "gaps"),
          deps.translate("AI_CHAT/TARGET_LABEL_THIS_REQUEST"),
        )
      }
      return {
        status:
          failed.length === 0
            ? "accepted"
            : accepted.length
              ? "partially_accepted"
              : "not_accepted",
        accepted,
        failed,
        note:
          (accepted.length
            ? `${accepted.length} aged answer(s) now count as current and no longer read as gaps. The ` +
              "cited evidence is still expired — this is a judgement recorded under the user's name, not " +
              "fresh data. "
            : "Nothing was accepted. ") +
          (failed.length
            ? "Report the failures individually with their reasons."
            : ""),
      }
    },
  },

  generate_conformity_declarations: {
    confirm: async (toolCall, deps) => {
      const target = await conformityTargets(toolCall.input)
      if ("error" in target) {
        // `run` refuses with the reason; a dialog first would ask the user to
        // approve a call that cannot happen.
        return null
      }
      // The gap count is the whole point of the dialog: an incomplete document is
      // exactly what a customer must not be told is compliant. A preview that
      // fails degrades to the plain copy rather than blocking behind a read.
      let gapLine = ""
      const { evaluations, refusal } = await previewEveryTarget(target.targets)
      // A refused pick fails the write too: `run` reports why, no dialog first.
      if (refusal) return null
      const gaps = evaluations ? gapArticleCount(evaluations) : 0
      if (gaps > 0) {
        gapLine = `\n\n${deps.translate("AI_CHAT/CONFIRM_CONF_GENERATE_GAPS", {
          count: gaps,
        })}`
      }
      return {
        title: deps.translate("AI_CHAT/CONFIRM_CONF_GENERATE_TITLE", {
          count: target.targets.length,
        }),
        detail:
          deps.translate("AI_CHAT/CONFIRM_CONF_GENERATE_DETAIL") + gapLine,
      }
    },
    run: async (toolCall, deps) => {
      const target = await conformityTargets(toolCall.input)
      if ("error" in target) {
        return target
      }
      // One call per target, in order: customer scope is one document per
      // customer, and `generate_v2` takes a single customer. Each call commits
      // on its own, so a failure half way through leaves real drafts behind —
      // caught here rather than thrown, or executeToolCall would report a plain
      // error and the drafts that DO exist would be invisible: not in the
      // result, not navigated to, and not refetched into the list.
      //
      // The confirmation is skipped for a refused pick (nothing to approve), so the
      // whole batch is checked before the first write: a pick one customer cannot
      // carry must not leave the customers before it with drafts nobody approved.
      // Every preview is settled, so a refusal is found whichever target failed first.
      const preflight = await previewEveryTarget(target.targets)
      if (preflight.refusal) return { error: preflight.refusal }
      let evaluations = preflight.evaluations
      const ids: string[] = []
      let failed: { subject: string; message: string } | null = null
      for (const one of target.targets) {
        try {
          const result = await generateConformityV2Request(one)
          ids.push(...result.declarationIds)
        } catch (error) {
          failed = {
            subject: one.scope === "customer" ? one.customerId : one.scope,
            message:
              regulationPickRefusal(error) ??
              (error instanceof Error ? error.message : String(error)),
          }
          break
        }
      }
      invalidateConformityList(deps.queryClient)
      if (ids.length) {
        offerNavigation(
          deps,
          conformityBatchPath(ids),
          deps.translate("AI_CHAT/TARGET_LABEL_THESE_DRAFTS"),
        )
      }
      // v2 generate returns ids only, so the gap warning comes from the evaluation
      // rather than the write: the one read before the writes, else a second try.
      // Best-effort: the drafts exist either way, and a failed read must not report
      // them as complete.
      let incomplete: number | null
      try {
        evaluations ??= await Promise.all(
          target.targets.map(previewConformityV2Request),
        )
        incomplete = gapArticleCount(evaluations)
      } catch {
        incomplete = null
      }
      return {
        status: failed
          ? "partially_drafted"
          : ids.length
            ? "drafted"
            : "nothing_drafted",
        // Named so the model reports what DID land before it retries: the
        // drafts below exist, and generate_v2 refreshes an existing open draft
        // rather than adding a second, so retrying the whole set is safe.
        ...(failed ? { failed_after: ids.length, failure: failed } : {}),
        // From the RESPONSE, not the input: re-generating for a subject that
        // already has an open draft refreshes that one instead of adding a second.
        drafted: ids.length,
        declaration_ids: ids.slice(0, MAX_LISTED_ITEMS),
        incomplete_articles: incomplete,
        note:
          `${ids.length} DRAFT declaration(s) — nothing is approved and nothing is sent. ` +
          "Each one covers the regulations picked, or without a pick the organization's own set; " +
          "the editor is where a regulation is deselected. " +
          (incomplete
            ? `${incomplete} article(s) across them have no verdict data, so those blocks print ` +
              '"Assessment incomplete" instead of a clean compliance statement. Say that before anyone ' +
              "approves one. "
            : "") +
          "Approving and sending are screen actions — use open_conformity_declaration with control " +
          '"approve".',
      }
    },
  },

  set_conformity_recipients: {
    confirm: async (toolCall, deps) => {
      const id = str(toolCall.input.declaration_id)
      const add = idList(toolCall.input.add).map(normaliseEmail)
      const remove = idList(toolCall.input.remove).map(normaliseEmail)
      if (!id || (add.length === 0 && remove.length === 0)) {
        return null
      }
      const { text } = changeLines(
        [
          [deps.translate("MC_INQ_DRAFT_ADD"), add.length ? add : undefined],
          [
            deps.translate("AI_CHAT/QUEUE/REMOVE"),
            remove.length ? remove : undefined,
          ],
        ],
        deps.translate,
      )
      return {
        title: deps.translate("AI_CHAT/CONFIRM_CONF_RECIPIENTS_TITLE"),
        detail: `${deps.translate("AI_CHAT/CONFIRM_CONF_RECIPIENTS_DETAIL")}\n\n${text}`,
      }
    },
    run: async (toolCall, deps) => {
      const id = str(toolCall.input.declaration_id)
      if (!id) {
        return { error: 'Missing "declaration_id".' }
      }
      const add = idList(toolCall.input.add).map(normaliseEmail)
      const remove = idList(toolCall.input.remove).map(normaliseEmail)
      if (add.length === 0 && remove.length === 0) {
        return {
          error:
            'Nothing to change. Pass "add", "remove", or both — addresses as the user gave them.',
        }
      }
      const detail = await fetchConformityDetail(id)
      const current = (detail.extraRecipientEmails ?? []).map(normaliseEmail)
      const primary = detail.recipientEmail
        ? normaliseEmail(detail.recipientEmail)
        : null

      // The customer's own contact lives on mc_customer_contacts and is merged
      // into `to` at send time, so it is neither on this list nor removable here —
      // and adding it would put the same address in `to` twice.
      const primaryRefused = [...remove, ...add].filter(
        (email) => email === primary,
      )
      const addable = add.filter((email) => email !== primary)
      const alreadyPresent = addable.filter((email) => current.includes(email))
      const notPresent = remove.filter(
        (email) => email !== primary && !current.includes(email),
      )
      const next = [
        ...current.filter((email) => !remove.includes(email)),
        ...addable.filter((email) => !current.includes(email)),
      ]
      if (
        next.length === current.length &&
        next.every((e, i) => e === current[i])
      ) {
        return {
          status: "unchanged",
          recipients_now: recipientsNow(primary, current),
          already_present: alreadyPresent,
          not_present: notPresent,
          primary_cannot_be_removed: primaryRefused,
          note:
            (primaryRefused.length
              ? "The customer's own primary contact already receives this declaration and is resolved " +
                "separately, so it is neither added to nor removed from this list; it is changed on the " +
                "customer record. "
              : "") +
            "Nothing was written. Say what the list holds rather than reporting a change.",
        }
      }
      const saved = await updateConformityRecipientsRequest(id, next)
      invalidateConformityDeclaration(deps.queryClient, id)
      offerNavigation(
        deps,
        conformityEditPath(id),
        deps.translate("AI_CHAT/TARGET_LABEL_THIS_DECLARATION"),
      )
      return {
        status: "updated",
        // From the response, so the reported list is what the server stored after
        // its own trim, lowercase and dedup — not what was sent.
        recipients_now: recipientsNow(
          primary,
          saved.extraRecipientEmails ?? [],
        ),
        added: addable.filter((email) => !current.includes(email)),
        removed: remove.filter((email) => current.includes(email)),
        already_present: alreadyPresent,
        not_present: notPresent,
        primary_cannot_be_removed: primaryRefused,
        note:
          "Everyone on that list receives the declaration WHEN IT IS SENT — this changed who would " +
          "receive it, and sent nothing. " +
          (primaryRefused.length
            ? "The customer's own primary contact already receives this declaration and is resolved " +
              "separately, so it is neither added to nor removed from this list; it is changed on the " +
              "customer record. "
            : "") +
          (detail.status === "sent"
            ? "This declaration has already been sent, so the people just added have NOT received it — " +
              "someone would have to send it again."
            : ""),
      }
    },
  },

  draft_conformity_cover_note: {
    confirm: (toolCall, deps) => {
      const subject = str(toolCall.input.subject)
      const body = str(toolCall.input.body)
      if (!str(toolCall.input.declaration_id) || (!subject && !body)) {
        return null
      }
      const { text } = changeLines(
        [
          [deps.translate("MC_INQ_DRAFT_SEC_SUBJECT"), subject],
          [deps.translate("MC_INQ_DRAFT_SEC_BODY"), body],
        ],
        deps.translate,
      )
      return {
        title: deps.translate("AI_CHAT/CONFIRM_CONF_COVER_TITLE"),
        detail: `${deps.translate("AI_CHAT/CONFIRM_CONF_COVER_DETAIL")}\n\n${text}`,
      }
    },
    run: async (toolCall, deps) => {
      const id = str(toolCall.input.declaration_id)
      if (!id) {
        return { error: 'Missing "declaration_id".' }
      }
      const subject = str(toolCall.input.subject)
      const body = str(toolCall.input.body)
      if (!subject && !body) {
        return {
          error:
            'Nothing to write. Pass "subject", "body", or both — an omitted half is left as stored.',
        }
      }
      const detail = await fetchConformityDetail(id)
      // The endpoint 409s LOCKED here; refusing first says which state locked it,
      // and why that copy is worth keeping.
      if (detail.status === "sent" || detail.status === "downloaded") {
        return {
          error:
            `This declaration has been ${detail.status}, so its covering email is locked — that copy is ` +
            "the record of what went out. Generating a fresh declaration is how a new covering email " +
            "gets written.",
        }
      }
      const saved = await updateCoverNoteRequest(id, {
        coverSubject: subject,
        coverBody: body,
      })
      invalidateConformityDeclaration(deps.queryClient, id)
      offerNavigation(
        deps,
        conformityEditPath(id),
        deps.translate("AI_CHAT/TARGET_LABEL_THIS_DECLARATION"),
      )
      return {
        status: "saved",
        // From the response: an empty string clears a field server-side, so what
        // was sent is not always what is stored.
        subject: saved.coverSubject,
        body_saved: !!saved.coverBody,
        note:
          "Saved on the declaration, and nothing was sent. The organization's signature is appended " +
          "when it goes out, so the body above is deliberately unsigned. " +
          (detail.status === "draft"
            ? "Approving the declaration keeps this text rather than re-drafting it."
            : "It stays editable until the declaration is sent or downloaded."),
      }
    },
  },
  cancel_mc_import: {
    confirm: async (toolCall, deps) => {
      const id = str(toolCall.input.import_id)
      const file = id ? await importFile(deps, id) : undefined
      // No dialog when `run` will refuse anyway — asking someone to approve a
      // change that cannot happen teaches them the dialog means nothing.
      if (!file || cancelImportRefusal(file)) {
        return null
      }
      return {
        title: deps.translate("MC_MD_IMPORT_REMOVE_TITLE"),
        detail: deps.translate("AI_CHAT/CONFIRM_CANCEL_IMPORT_DETAIL", {
          fileName: file.filename,
          status: deps.translate(importStatusDisplay(file).key),
        }),
      }
    },
    run: async (toolCall, deps) => {
      const id = str(toolCall.input.import_id)
      if (!id) {
        return { error: 'Missing "import_id". Get one from list_mc_imports.' }
      }
      const file = await importFile(deps, id)
      if (!file) {
        return {
          error: `No import ${id} in this organization. Get an id from list_mc_imports.`,
        }
      }
      const refusal = cancelImportRefusal(file)
      if (refusal) {
        return { error: refusal }
      }
      await importsApi.deleteFile(id)
      invalidateImportFiles(deps.queryClient)
      return {
        // NOT "cancelled" — that word means "nothing happened" to the write
        // guard (rejected_by_user, not_accepted, unchanged, …), and this is a
        // real DELETE: the file and its staged rows are gone. Sharing the word
        // let a genuine write get classified as a no-op and replaced with "no
        // supported write action was successfully executed" on the one turn
        // where a write actually landed.
        status: "discarded",
        import_id: id,
        file_name: file.filename,
        note:
          "The file and its staged rows are gone. Nothing was written to master data by this import, " +
          "so nothing there changed. The file can be uploaded again.",
      }
    },
  },
}

/**
 * Which settings page a change belongs to, so the affordance lands on the
 * setting that moved. Mixed changes get the first match — no page shows all of
 * them.
 */
function settingsPathFor(changed: string[]): string {
  if (changed.includes("emailTemplates")) {
    return settingsTargetPath("inquiry.firstEmail")
  }
  if (changed.includes("defaultCc")) {
    return settingsTargetPath("inquiry.cc")
  }
  if (changed.includes("emailSignatures")) {
    return settingsTargetPath("email.signature")
  }
  if (
    changed.some((field) =>
      ["remindersEnabled", "reminderIntervalDays", "maxReminders"].includes(
        field,
      ),
    )
  ) {
    return settingsTargetPath("inquiry.reminders")
  }
  return supplierInquiriesTabPath("attachment")
}

/**
 * The PATCH fields the endpoint only honours while the inquiry is a draft. On an
 * already-sent one they are accepted and silently dropped (see
 * `update_inquiry`'s `if is_draft:` block), which is why the result is derived
 * from the response rather than from what was requested.
 */
const DRAFT_ONLY_CHANGES = [
  "regulationKeys",
  "followOrgRegulations",
  "articleIds",
  "emailSubject",
  "emailBody",
  "language",
  "attachmentFormat",
]

/**
 * Changes that belong to the composer's reminder stage. When an edit touches
 * nothing else, the composer should open on that stage — landing on the first
 * email would show the user copy they did not change.
 */
const REMINDER_STAGE_CHANGES = ["reminderEmailSubject", "reminderEmailBody"]

/** Confirmation lines for a draft update, in the order the composer shows them. */
function inquiryDraftEntries(
  input: Record<string, unknown>,
  translate: ToolExecutorDeps["translate"],
): [string, unknown][] {
  return [
    // A reset to Settings is a scope change too, confirmed like a pick.
    [
      translate("MC_INQ_FRAMEWORKS_LABEL"),
      input.regulation_keys ??
        (input.follow_org_regulations === true
          ? translate("MC_INQ_REG_PICKER_FOLLOW_SETTINGS")
          : undefined),
    ],
    [translate("AI_CHAT/FIELD_ARTICLE_SCOPE"), input.article_ids],
    [translate("AI_CHAT/FIELD_EMAIL_SUBJECT"), input.email_subject],
    [translate("MC_INQ_DRAFT_SEC_BODY"), input.email_body],
    [translate("AI_CHAT/FIELD_REMINDER_SUBJECT"), input.reminder_email_subject],
    [translate("AI_CHAT/FIELD_REMINDER_BODY"), input.reminder_email_body],
    [translate("MC_INQ_DRAFT_TO"), input.recipients_to],
    [translate("MC_INQ_DRAFT_CC"), input.recipients_cc],
    [translate("AI_CHAT/FIELD_LANGUAGE"), input.language],
    [translate("AI_CHAT/FIELD_ATTACHMENT"), input.attachment_format],
    [translate("AI_CHAT/FIELD_DEADLINE"), input.due_at],
  ]
}

/**
 * What each draft really asks (TUR-1730): its format's share of the regulations, not
 * the keys named, and whether that format asks none of them, which blocks its Send
 * until the format or the selection changes.
 */
function draftRegulationReport(
  items: ReadonlyArray<{
    id: string
    attachmentFormat?: string | null
    askedRegulations?: string[] | null
    regulationScopeError?: string | null
  }>,
) {
  const drafts = items.map((d) => ({
    inquiry_id: d.id,
    attachment_format: d.attachmentFormat ?? null,
    asked_regulations: d.askedRegulations ?? null,
    send_blocked: !!d.regulationScopeError,
  }))
  const blocked = drafts.filter((d) => d.send_blocked)
  return {
    drafts,
    note:
      blocked.length > 0
        ? `Not sendable yet: ${blocked.length} draft(s) have send_blocked, because their format asks ` +
          "none of the chosen regulations. Tell the user to change the format or the selection for " +
          "those first; do not say pressing Send is the only step left. "
        : "",
  }
}

/**
 * The regulations an assistant write names (TUR-1730), checked against the
 * canonical keys before any request: an unknown key is an error naming the
 * keys, so the model retries with one instead of the API's bare 422.
 */
function regulationKeysInput(raw: unknown): {
  keys?: string[]
  error?: string
} {
  if (raw === undefined || raw === null) return {}
  const list = Array.isArray(raw) ? raw.map(String) : [String(raw)]
  const known = new Set<string>(INQUIRY_REGULATION_KEYS)
  const unknown = list.filter((key) => !known.has(key))
  if (unknown.length > 0 || list.length === 0) {
    return {
      error:
        `Unknown regulation key: ${unknown.join(", ") || "(none)"}. ` +
        `Use one or more of these keys exactly: ${INQUIRY_REGULATION_KEYS.join(", ")}.`,
    }
  }
  return { keys: [...new Set(list)] }
}

/** The PATCH body, camelCased for the API client's request interceptor. */
function toDraftChanges(
  input: Record<string, unknown>,
): Record<string, unknown> {
  const map: Record<string, string> = {
    article_ids: "articleIds",
    email_subject: "emailSubject",
    email_body: "emailBody",
    reminder_email_subject: "reminderEmailSubject",
    reminder_email_body: "reminderEmailBody",
    recipients_to: "recipientsTo",
    recipients_cc: "recipientsCc",
    language: "language",
    attachment_format: "attachmentFormat",
    due_at: "dueAt",
    regulation_keys: "regulationKeys",
    follow_org_regulations: "followOrgRegulations",
  }
  const changes: Record<string, unknown> = {}
  for (const [from, to] of Object.entries(map)) {
    if (input[from] !== undefined) {
      changes[to] = input[from]
    }
  }
  return changes
}

/**
 * Whether the model supplied a usable value for a BOUNDED field.
 *
 * `undefined` is "not mentioned". `null` is not a value either: an on/off switch
 * and a 1–90 day interval have no cleared state, and coercing null would write
 * `false` (silently turning the org's reminders off) or `0` (a 422). The
 * confirmation dialog reads the same predicate, so what is shown and what is
 * saved cannot drift apart.
 */
function isSupplied(value: unknown): boolean {
  return value !== undefined && value !== null
}

/** Passes a bounded value through only when it was really supplied. */
function suppliedOnly(value: unknown): unknown {
  return isSupplied(value) ? value : undefined
}

/**
 * A clearable free-text field. `undefined` leaves it alone; `null` and `""` both
 * mean clear, which is what `formatValue` already shows as "(cleared)".
 */
function clearableText(value: unknown): string | undefined {
  if (value === undefined) return undefined
  return value === null ? "" : String(value)
}

/** One `email_templates` entry as the model sends it. */
interface TemplateCellInput {
  stage?: unknown
  language?: unknown
  subject?: unknown
  body?: unknown
}

function templateCells(input: Record<string, unknown>): TemplateCellInput[] {
  const raw = input.email_templates
  return (Array.isArray(raw) ? raw : []).filter(
    (cell): cell is TemplateCellInput =>
      typeof cell === "object" && cell !== null,
  )
}

const TEMPLATE_STAGES = ["first", "reminder"] as const
const TEMPLATE_LANGS = ["en", "de"] as const

type TemplateStage = (typeof TEMPLATE_STAGES)[number]
type TemplateLang = (typeof TEMPLATE_LANGS)[number]

function templateStage(value: unknown): TemplateStage | null {
  return TEMPLATE_STAGES.find((stage) => stage === value) ?? null
}

function templateLang(value: unknown): TemplateLang | null {
  return TEMPLATE_LANGS.find((lang) => lang === value) ?? null
}

/**
 * The requested cells as a template patch, cell-wise: the endpoint writes this
 * blob verbatim, so anything not named must survive untouched. A reminder SUBJECT
 * is dropped (the outbound one is always "Re: …") and reported as ignored.
 */
function toEmailTemplatesPatch(
  input: Record<string, unknown>,
): EmailTemplatesPatch | undefined {
  const patch: EmailTemplatesPatch = {}
  for (const cell of templateCells(input)) {
    const stage = templateStage(cell.stage)
    const lang = templateLang(cell.language)
    if (!stage || !lang) {
      continue
    }
    const copy: { subject?: string; body?: string } = {}
    if (stage === "first" && isSupplied(cell.subject)) {
      copy.subject = String(cell.subject)
    }
    if (isSupplied(cell.body)) {
      copy.body = String(cell.body)
    }
    if (Object.keys(copy).length === 0) {
      continue
    }
    patch[stage] = { ...(patch[stage] ?? {}), [lang]: copy }
  }
  return Object.keys(patch).length ? patch : undefined
}

/** Languages whose reminder SUBJECT was asked for and dropped — see above. */
function reminderSubjectCells(input: Record<string, unknown>): string[] {
  return templateCells(input)
    .filter(
      (cell) =>
        templateStage(cell.stage) === "reminder" && isSupplied(cell.subject),
    )
    .map((cell) => String(cell.language))
}

/** "First email subject + body (de)" — how a changed template cell reads in the dialog. */
function templateCellLabels(
  input: Record<string, unknown>,
  translate: ToolExecutorDeps["translate"],
): string[] {
  const patch = toEmailTemplatesPatch(input) ?? {}
  const labels: string[] = []
  for (const stage of TEMPLATE_STAGES) {
    for (const lang of TEMPLATE_LANGS) {
      const copy = patch[stage]?.[lang]
      if (!copy) {
        continue
      }
      const halves = [
        copy.subject !== undefined
          ? translate("AI_CHAT/FIELD_SUBJECT_WORD")
          : null,
        copy.body !== undefined ? translate("AI_CHAT/FIELD_BODY_WORD") : null,
      ].filter(Boolean)
      const stageLabel = translate(
        stage === "first"
          ? "AI_CHAT/TEMPLATE_STAGE_FIRST_EMAIL"
          : "AI_CHAT/TEMPLATE_STAGE_REMINDER_EMAIL",
      )
      labels.push(`${stageLabel} ${halves.join(" + ")} (${lang})`)
    }
  }
  return labels
}

function templateSettingsEntries(
  input: Record<string, unknown>,
  translate: ToolExecutorDeps["translate"],
): [string, unknown][] {
  const cells = templateCellLabels(input, translate)
  return [
    // undefined, not the empty list: `changeLines` drops undefined but renders an
    // empty array as "(cleared)", which would read as "the copy was wiped".
    [translate("AI_CHAT/FIELD_EMAIL_COPY"), cells.length ? cells : undefined],
    [translate("AI_CHAT/FIELD_QUESTIONNAIRE_FOOTER"), input.footer],
    [translate("AI_CHAT/FIELD_CONTACT_PERSON"), input.contact_person],
    [
      translate("AI_CHAT/FIELD_EMAIL_SIGNATURE"),
      isSupplied(input.email_signatures)
        ? Object.keys(input.email_signatures as object).join(", ")
        : undefined,
    ],
    [translate("AI_CHAT/FIELD_DEFAULT_CC"), input.default_cc],
    [
      translate("AI_CHAT/FIELD_AUTO_REMINDERS"),
      boolLabel(input.reminders_enabled, translate),
    ],
    [
      translate("AI_CHAT/FIELD_REMINDER_INTERVAL"),
      suppliedOnly(input.reminder_interval_days),
    ],
    [
      translate("AI_CHAT/FIELD_MAX_REMINDERS"),
      suppliedOnly(input.max_reminders),
    ],
  ]
}

function boolLabel(
  value: unknown,
  translate: ToolExecutorDeps["translate"],
): string | undefined {
  if (!isSupplied(value)) return undefined
  return translate(value ? "AI_CHAT/BOOL_ON" : "AI_CHAT/BOOL_OFF")
}

function toTemplateSettingsPatch(
  input: Record<string, unknown>,
): TemplateSettingsPatch {
  const patch: TemplateSettingsPatch = {}
  const emailTemplates = toEmailTemplatesPatch(input)
  if (emailTemplates) {
    patch.emailTemplates = emailTemplates
  }
  const footer = clearableText(input.footer)
  if (footer !== undefined) {
    patch.questionnaireFooter = footer
  }
  const contactPerson = clearableText(input.contact_person)
  if (contactPerson !== undefined) {
    patch.contactPerson = contactPerson
  }
  // A partial map is fine: saveTemplateSettings merges it per language key. There
  // is no "cleared" state for the map itself, so null counts as not supplied.
  if (isSupplied(input.email_signatures)) {
    patch.emailSignatures = input.email_signatures as Partial<{
      en: string
      de: string
    }>
  }
  // null clears the list, matching the "(cleared)" the dialog showed. Without the
  // explicit [], `patch.defaultCc ?? base.defaultCc` would keep the old addresses
  // after the user approved removing them.
  if (input.default_cc !== undefined) {
    patch.defaultCc =
      input.default_cc === null ? [] : (input.default_cc as string[])
  }
  if (isSupplied(input.reminders_enabled)) {
    patch.remindersEnabled = Boolean(input.reminders_enabled)
  }
  if (isSupplied(input.reminder_interval_days)) {
    patch.reminderIntervalDays = Number(input.reminder_interval_days)
  }
  if (isSupplied(input.max_reminders)) {
    patch.maxReminders = Number(input.max_reminders)
  }
  return patch
}
